import React, { useState, useEffect } from 'react';

// Print styles
const printStyles = `
@media print {
  @page { margin: 10mm; size: landscape; }
  body { background-color: white !important; color: black !important; font-size: 10pt !important; }
  header, .no-print { display: none !important; }
  main { padding: 0 !important; max-widdth: 100% !important; }
  .shadow-sm, .shadow-md, .shadow-lg { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  .rounded-xl { border-radius: 4px !important; }
  .p-6, .p-5, .p-4 { padding: 8px !important; }
  .gap-6 { gap: 8px !important; }
  .mb-8, .mb-6 { margin-bottom: 8px !important; }
  .text-3xl { font-size: 16px !important; }
  .text-2xl { font-size: 14px !important; }
  .text-xl { font-size: 12px !important; }
  table { font-size: 9pt !important; }
  td, th { padding: 4px 8px !important; }
  .overflow-x-auto { overflow: visible !important; }
}
`;

// Simple icons as SVG components (instead of lucide-react)
const ChevronRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6"/>
  </svg>
);

const ChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6"/>
  </svg>
);

const BarChart2 = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" x2="18" y1="20" y2="10"/><line x1="12" x2="12" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="14"/>
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><line x1="3" x2="3.01" y1="6" y2="6"/><line x1="3" x2="3.01" y1="12" y2="12"/><line x1="3" x2="3.01" y1="18" y2="18"/>
  </svg>
);

// Formatters
const formatMoney = (value) => {
  const num = isNaN(value) ? 0 : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(num);
};

const formatMoneyCompact = (value) => {
  const num = isNaN(value) ? 0 : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(num);
};

// Parse date string as local time to avoid timezone shift
// "2026-02-28" should be Feb 28, not Feb 27 (due to UTC interpretation)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

// Get date range for a KPI timeframe (mirrors server-side getDateRangeForTimeframe)
const getDateRangeForTimeframe = (timeframe) => {
  const today = new Date();
  let startDate, endDate;
  switch (timeframe) {
    case 'Weekly':
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'Monthly':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    case 'Quarterly': {
      const quarter = Math.floor((today.getMonth() + 3) / 3);
      startDate = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
      endDate = new Date(today.getFullYear(), quarter * 3, 0, 23, 59, 59, 999);
      break;
    }
    default:
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
  }
  return { startDate, endDate };
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '-';
  const d = parseLocalDate(dateStr);
  if (!d || isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'America/New_York'
  });
};

const getStatusColor = (status) => {
  const colors = {
    'Identified': 'bg-gray-100 text-gray-700',
    'Qualified': 'bg-cyan-100 text-cyan-700',
    'Discovery': 'bg-cyan-200 text-cyan-800',
    'Pitch': 'bg-blue-100 text-blue-700',
    'Negotiation': 'bg-blue-200 text-blue-800',
    'Decision': 'bg-indigo-100 text-indigo-700',
    'Closed-Won': 'bg-green-100 text-green-700',
    'Closed-Lost': 'bg-red-100 text-red-700'
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
};

// Funnel stage configuration
const funnelConfig = [
  { id: 'Identified', label: 'Identified', probability: 0.05, color: '#9ca3af', width: '100%', clipPath: 'polygon(0 0, 100% 0, 93% 100%, 7% 100%)' },
  { id: 'Qualified', label: 'Qualified', probability: 0.10, color: '#67e8f9', width: '86%', clipPath: 'polygon(0 0, 100% 0, 91.86% 100%, 8.14% 100%)' },
  { id: 'Discovery', label: 'Discovery', probability: 0.25, color: '#22d3ee', width: '72%', clipPath: 'polygon(0 0, 100% 0, 90.28% 100%, 9.72% 100%)' },
  { id: 'Pitch', label: 'Pitch', probability: 0.50, color: '#0ea5e9', width: '58%', clipPath: 'polygon(0 0, 100% 0, 87.93% 100%, 12.07% 100%)' },
  { id: 'Negotiation', label: 'Negotiation', probability: 0.70, color: '#1d4ed8', width: '44%', clipPath: 'polygon(0 0, 100% 0, 84.09% 100%, 15.91% 100%)' },
  { id: 'Decision', label: 'Decision', probability: 0.90, color: '#0f172a', width: '30%', clipPath: 'polygon(0 0, 100% 0, 78.33% 100%, 21.67% 100%)' },
];

// Award icon for leaderboard
const AwardIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
  </svg>
);

// Trophy icon
const TrophyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500">
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
  </svg>
);

// Pie chart icon
const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
  </svg>
);

// Leaderboard View Component
function LeaderboardView({ deals, filterDivision, filterOwner, filterDateFrom, filterDateTo, userDepartments = {} }) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentQuarter = Math.ceil((today.getMonth() + 1) / 3);

  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter); // 1-4, null = all time
  const [selectedMonth, setSelectedMonth] = useState(null); // 0-11
  const [selectedWeek, setSelectedWeek] = useState(null); // 1-4

  // Quarter → months mapping
  const quarterMonths = {
    1: [{ idx: 0, label: 'January' }, { idx: 1, label: 'February' }, { idx: 2, label: 'March' }],
    2: [{ idx: 3, label: 'April' }, { idx: 4, label: 'May' }, { idx: 5, label: 'June' }],
    3: [{ idx: 6, label: 'July' }, { idx: 7, label: 'August' }, { idx: 8, label: 'September' }],
    4: [{ idx: 9, label: 'October' }, { idx: 10, label: 'November' }, { idx: 11, label: 'December' }],
  };

  // Get week date range within a month (week 1=days 1-7, 2=8-14, 3=15-21, 4=22-end)
  const getWeekRange = (year, monthIdx, week) => {
    const startDay = (week - 1) * 7 + 1;
    const endDay = week === 4 ? new Date(year, monthIdx + 1, 0).getDate() : week * 7;
    return {
      from: new Date(year, monthIdx, startDay),
      to: new Date(year, monthIdx, endDay, 23, 59, 59, 999),
    };
  };

  // Compute active period date range (overrides global date filter when a period is selected)
  const periodRange = (() => {
    if (selectedQuarter === null) return null; // use global filters
    if (selectedMonth !== null) {
      if (selectedWeek !== null) return getWeekRange(currentYear, selectedMonth, selectedWeek);
      return { from: new Date(currentYear, selectedMonth, 1), to: new Date(currentYear, selectedMonth + 1, 0, 23, 59, 59, 999) };
    }
    const qStart = (selectedQuarter - 1) * 3;
    return { from: new Date(currentYear, qStart, 1), to: new Date(currentYear, qStart + 3, 0, 23, 59, 59, 999) };
  })();

  // Period label for display
  const periodLabel = (() => {
    if (selectedQuarter === null) return 'All Time';
    if (selectedMonth !== null) {
      const mName = quarterMonths[selectedQuarter].find(m => m.idx === selectedMonth)?.label || '';
      if (selectedWeek !== null) return `${mName} — Week ${selectedWeek}`;
      return mName;
    }
    return `Q${selectedQuarter} ${currentYear}`;
  })();

  // Helper to get effective division (considering department overrides)
  const getEffectiveDivision = (deal) => userDepartments[deal.owner] || deal.division;

  // Apply division/owner filters
  let filteredDeals = deals;
  if (filterDivision) {
    filteredDeals = filteredDeals.filter(d => getEffectiveDivision(d) === filterDivision);
  }
  if (filterOwner) {
    filteredDeals = filteredDeals.filter(d => d.owner === filterOwner);
  }

  // Apply date filter: period picker takes priority; fall back to global date filters
  if (periodRange) {
    filteredDeals = filteredDeals.filter(d => {
      const closeDate = d.actualClose ? parseLocalDate(d.actualClose) : null;
      if (!closeDate || isNaN(closeDate.getTime())) return false;
      return closeDate >= periodRange.from && closeDate <= periodRange.to;
    });
  } else if (filterDateFrom || filterDateTo) {
    filteredDeals = filteredDeals.filter(d => {
      const closeDate = d.actualClose ? new Date(d.actualClose) : null;
      if (!closeDate || isNaN(closeDate.getTime())) return false;
      if (filterDateFrom && closeDate < new Date(filterDateFrom)) return false;
      if (filterDateTo && closeDate > new Date(filterDateTo)) return false;
      return true;
    });
  }
  const closedWonDeals = filteredDeals.filter(d => d.status === 'Closed-Won');

  // Calculate summary stats
  const totalRevenue = closedWonDeals.reduce((sum, d) => sum + d.value, 0);
  const totalWins = closedWonDeals.length;

  // Group by owner
  const ownerMap = {};
  const divisionMap = {};
  closedWonDeals.forEach(d => {
    const owner = d.owner || 'Unknown';
    const division = d.division || 'Other';
    if (!ownerMap[owner]) ownerMap[owner] = { name: owner, revenue: 0, count: 0, division };
    ownerMap[owner].revenue += d.value;
    ownerMap[owner].count += 1;
    if (!divisionMap[division]) divisionMap[division] = { revenue: 0, count: 0 };
    divisionMap[division].revenue += d.value;
    divisionMap[division].count += 1;
  });

  const top3Revenue = Object.values(ownerMap).sort((a, b) => b.revenue - a.revenue).slice(0, 3);
  const top3Volume = Object.values(ownerMap).sort((a, b) => b.count - a.count).slice(0, 3);
  const serviceLineStats = Object.entries(divisionMap).map(([name, stats]) => ({ name, ...stats })).sort((a, b) => b.revenue - a.revenue);

  // Last week date range (Mon–Sun)
  const lastWeekRange = (() => {
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
    const start = new Date(today.getFullYear(), today.getMonth(), diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  })();

  const lastWeekDeals = filteredDeals.filter(d => {
    if (d.status !== 'Closed-Won') return false;
    const closeDate = d.actualClose ? parseLocalDate(d.actualClose) : null;
    if (!closeDate || isNaN(closeDate.getTime())) return false;
    return closeDate >= lastWeekRange.start && closeDate <= lastWeekRange.end;
  });

  const buildStats = (wonDeals) => {
    const om = {}, dm = {};
    wonDeals.forEach(d => {
      const owner = d.owner || 'Unknown';
      const division = d.division || 'Other';
      if (!om[owner]) om[owner] = { name: owner, revenue: 0, count: 0, division };
      om[owner].revenue += d.value;
      om[owner].count += 1;
      if (!dm[division]) dm[division] = { revenue: 0, count: 0 };
      dm[division].revenue += d.value;
      dm[division].count += 1;
    });
    return {
      totalRevenue: wonDeals.reduce((s, d) => s + d.value, 0),
      totalWins: wonDeals.length,
      top3Revenue: Object.values(om).sort((a, b) => b.revenue - a.revenue).slice(0, 3),
      top3Volume: Object.values(om).sort((a, b) => b.count - a.count).slice(0, 3),
      serviceLineStats: Object.entries(dm).map(([name, s]) => ({ name, ...s })).sort((a, b) => b.revenue - a.revenue),
    };
  };

  const lwStats = buildStats(lastWeekDeals);
  const lwLabel = `${lastWeekRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${lastWeekRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
        {/* Quarter row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">Quarter</span>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => { setSelectedQuarter(null); setSelectedMonth(null); setSelectedWeek(null); }}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedQuarter === null ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            >All Time</button>
            {[1, 2, 3, 4].map(q => (
              <button
                key={q}
                onClick={() => { setSelectedQuarter(q); setSelectedMonth(null); setSelectedWeek(null); }}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedQuarter === q ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              >Q{q}</button>
            ))}
          </div>
        </div>

        {/* Month row — shown when a quarter is selected */}
        {selectedQuarter !== null && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">Month</span>
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => { setSelectedMonth(null); setSelectedWeek(null); }}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedMonth === null ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              >All</button>
              {quarterMonths[selectedQuarter].map(m => (
                <button
                  key={m.idx}
                  onClick={() => { setSelectedMonth(m.idx); setSelectedWeek(null); }}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedMonth === m.idx ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                >{m.label.slice(0, 3)}</button>
              ))}
            </div>
          </div>
        )}

        {/* Week row — shown when a month is selected */}
        {selectedMonth !== null && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 w-16 shrink-0">Week</span>
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={() => setSelectedWeek(null)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedWeek === null ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              >All</button>
              {[1, 2, 3, 4].map(w => {
                const wr = getWeekRange(currentYear, selectedMonth, w);
                const label = `${wr.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${wr.to.toLocaleDateString('en-US', { day: 'numeric' })}`;
                return (
                  <button
                    key={w}
                    onClick={() => setSelectedWeek(w)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${selectedWeek === w ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  >Wk {w} <span className="font-normal opacity-70">({label})</span></button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active period label */}
        <div className="text-xs text-slate-400 pt-1 border-t border-slate-100">
          Showing: <span className="font-semibold text-slate-600">{periodLabel}</span>
          {periodRange && (
            <span className="ml-2 opacity-70">
              ({periodRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {periodRange.to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})
            </span>
          )}
        </div>
      </div>

      {/* Top Cards - V4 Style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Revenue Leaderboard */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-700 flex items-center gap-2">
              <TrophyIcon />
              Top Revenue
            </h3>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">
              {formatMoneyCompact(totalRevenue)} Total
            </span>
          </div>
          <div className="p-4 flex-1">
            {top3Revenue.length === 0 ? (
              <div className="text-center text-slate-400 italic text-sm py-4">No data</div>
            ) : (
              <ul className="space-y-3">
                {top3Revenue.map((p, i) => (
                  <li key={p.name} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                      <div>
                        <div className="font-bold text-slate-700">{p.name}</div>
                        <div className="text-[10px] text-slate-400">{p.division}</div>
                      </div>
                    </div>
                    <div className="font-mono font-bold text-emerald-600">{formatMoneyCompact(p.revenue)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Volume Leaderboard */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <h3 className="font-bold text-slate-700 flex items-center gap-2">
              <BriefcaseIcon />
              Top Volume
            </h3>
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-100">
              {totalWins} Deals
            </span>
          </div>
          <div className="p-4 flex-1">
            {top3Volume.length === 0 ? (
              <div className="text-center text-slate-400 italic text-sm py-4">No data</div>
            ) : (
              <ul className="space-y-3">
                {top3Volume.map((p, i) => (
                  <li key={p.name} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                      <div>
                        <div className="font-bold text-slate-700">{p.name}</div>
                        <div className="text-[10px] text-slate-400">{p.division}</div>
                      </div>
                    </div>
                    <div className="font-bold text-blue-600">{p.count} <span className="text-[10px] font-normal text-slate-400">deals</span></div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Business Lines */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <h3 className="font-bold text-slate-700 flex items-center gap-2">
              <PieChartIcon />
              Business Lines
            </h3>
          </div>
          <div className="p-4 flex-1 overflow-y-auto max-h-[300px]">
            {serviceLineStats.length === 0 ? (
              <div className="text-center text-slate-400 italic text-sm py-4">No data</div>
            ) : (
              <ul className="space-y-3">
                {serviceLineStats.map((d, i) => (
                  <li key={d.name} className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                      <span className="font-medium text-slate-700">{d.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-emerald-600">{formatMoneyCompact(d.revenue)}</div>
                      <div className="text-[10px] text-slate-400">{d.count} deals</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Last Week Cards */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Last Week</h3>
          <span className="text-xs text-slate-400">{lwLabel}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-700 flex items-center gap-2"><TrophyIcon />Top Revenue</h3>
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded border border-emerald-100">{formatMoneyCompact(lwStats.totalRevenue)} Total</span>
            </div>
            <div className="p-4 flex-1">
              {lwStats.top3Revenue.length === 0 ? (
                <div className="text-center text-slate-400 italic text-sm py-4">No wins last week</div>
              ) : (
                <ul className="space-y-3">
                  {lwStats.top3Revenue.map((p, i) => (
                    <li key={p.name} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                        <div>
                          <div className="font-bold text-slate-700">{p.name}</div>
                          <div className="text-[10px] text-slate-400">{p.division}</div>
                        </div>
                      </div>
                      <div className="font-mono font-bold text-emerald-600">{formatMoneyCompact(p.revenue)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-700 flex items-center gap-2"><BriefcaseIcon />Top Volume</h3>
              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded border border-blue-100">{lwStats.totalWins} Deals</span>
            </div>
            <div className="p-4 flex-1">
              {lwStats.top3Volume.length === 0 ? (
                <div className="text-center text-slate-400 italic text-sm py-4">No wins last week</div>
              ) : (
                <ul className="space-y-3">
                  {lwStats.top3Volume.map((p, i) => (
                    <li key={p.name} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                        <div>
                          <div className="font-bold text-slate-700">{p.name}</div>
                          <div className="text-[10px] text-slate-400">{p.division}</div>
                        </div>
                      </div>
                      <div className="font-bold text-blue-600">{p.count} <span className="text-[10px] font-normal text-slate-400">deals</span></div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-100 bg-slate-50">
              <h3 className="font-bold text-slate-700 flex items-center gap-2"><PieChartIcon />Business Lines</h3>
            </div>
            <div className="p-4 flex-1 overflow-y-auto max-h-[300px]">
              {lwStats.serviceLineStats.length === 0 ? (
                <div className="text-center text-slate-400 italic text-sm py-4">No wins last week</div>
              ) : (
                <ul className="space-y-3">
                  {lwStats.serviceLineStats.map((d) => (
                    <li key={d.name} className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                        <span className="font-medium text-slate-700">{d.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-emerald-600">{formatMoneyCompact(d.revenue)}</div>
                        <div className="text-[10px] text-slate-400">{d.count} deals</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Won Deal Log - V4 Style */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b bg-slate-50 flex justify-between items-center">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <ListIcon />
            Won Deal Log
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200">
              <tr>
                <th className="px-6 py-3">Deal Name</th>
                <th className="px-6 py-3">Owner</th>
                <th className="px-6 py-3 text-center">Close Date</th>
                <th className="px-6 py-3 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {closedWonDeals.length === 0 ? (
                <tr><td colSpan="4" className="p-10 text-center text-slate-400">No wins recorded</td></tr>
              ) : (
                closedWonDeals.map(deal => (
                  <tr key={deal.id} className="hover:bg-slate-50">
                    <td className="px-6 py-3 font-medium text-slate-900">{deal.title}</td>
                    <td className="px-6 py-3 text-slate-600">{deal.owner}</td>
                    <td className="px-6 py-3 text-center text-slate-600">{formatDateShort(deal.actualClose)}</td>
                    <td className="px-6 py-3 text-right font-mono font-bold text-emerald-600">{formatMoney(deal.value)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Settings/Gear icon for admin mode
const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

// Clock icon for timeframe
const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

// Target icon for KPIs
const TargetIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
  </svg>
);

// KPI Card Component
function KPICard({ owner, division, data, goals, timeframe, onClick }) {
  const getTarget = (metric) => {
    const qGoal = (goals && goals[metric]) || 0;
    if (timeframe === 'Quarterly') return qGoal;
    if (timeframe === 'Monthly') return Math.round(qGoal / 3);
    if (timeframe === 'Weekly') return Math.round(qGoal / 13);
    return 0;
  };

  const metrics = [
    { label: 'Calls', key: 'calls', val: data.calls || 0, target: getTarget('calls') },
    { label: 'Meetings', key: 'meetings', val: data.meetings || 0, target: getTarget('meetings') },
    { label: 'Added', key: 'added', val: data.opportunitiesAdded || 0, target: getTarget('added') },
    { label: 'Won (Count)', key: 'won', val: data.wins || 0, target: getTarget('won') },
  ];

  const revGoal = getTarget('revenue');
  const revPct = revGoal > 0 ? Math.min((data.revenue / revGoal) * 100, 100) : 0;
  const qRevGoal = (goals && goals.revenue) || 0;
  const pipeCoverage = qRevGoal > 0 ? (data.pipeline / qRevGoal).toFixed(1) : '0.0';

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4 flex flex-col h-full cursor-pointer hover:shadow-md hover:border-blue-300 transition-all" onClick={onClick}>
      {/* Header - V4 Style */}
      <h4 className="font-bold text-slate-800 mb-3 text-lg flex justify-between items-center">
        {owner}
        <span className="text-xs font-normal text-slate-400 bg-slate-50 px-2 py-1 rounded">{timeframe}</span>
      </h4>

      {/* Metrics - V4 Style vertical list with full-width progress bars */}
      <div className="space-y-4 flex-1">
        {metrics.map(m => {
          const pct = m.target > 0 ? Math.min((m.val / m.target) * 100, 100) : 0;
          const barColor = pct >= 100 ? 'bg-emerald-500' : (pct >= 50 ? 'bg-blue-500' : 'bg-amber-400');
          return (
            <div key={m.key}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-500 font-medium">{m.label}</span>
                <span className="font-bold text-slate-700">{m.val} <span className="text-slate-400 font-normal">/ {m.target}</span></span>
              </div>
              <div className="relative w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                <div className={`h-3 rounded-full ${barColor}`} style={{ width: `${pct}%` }}></div>
                <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-slate-600 mix-blend-multiply">{pct.toFixed(0)}%</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Revenue Attainment + Pipeline Coverage - V4 Style */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="mb-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500 font-bold uppercase">Revenue Attainment</span>
            <span className="font-bold text-slate-700">{formatMoneyCompact(data.revenue || 0)} <span className="text-slate-400 font-normal">/ {formatMoneyCompact(revGoal)}</span></span>
          </div>
          <div className="relative w-full bg-slate-100 rounded-full h-3 overflow-hidden">
            <div className={`h-3 rounded-full ${revPct >= 100 ? 'bg-emerald-500' : 'bg-blue-600'}`} style={{ width: `${revPct}%` }}></div>
          </div>
        </div>
        <div className="flex justify-between items-center bg-slate-50 p-2 rounded border border-slate-200">
          <span className="text-xs font-bold text-slate-500 uppercase">Pipeline Coverage</span>
          <div className="text-right">
            <div className={`text-sm font-bold ${parseFloat(pipeCoverage) >= 3 ? 'text-emerald-600' : (parseFloat(pipeCoverage) >= 1.5 ? 'text-blue-600' : 'text-amber-500')}`}>{pipeCoverage}x</div>
            <div className="text-[9px] text-slate-400">Total Pipe: {formatMoneyCompact(data.pipeline || 0)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// KPI Detail Modal Component
function KPIDetailModal({ owner, timeframe, onClose }) {
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  // Strip HTML tags from comments and decode entities
  const stripHtml = (html) => {
    if (!html) return '-';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '-';
  };

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const tf = timeframe.toLowerCase();
        const response = await fetch(`/api/activities/detail?owner=${encodeURIComponent(owner)}&timeframe=${tf}`);
        const result = await response.json();
        if (result.success) {
          setDetails(result.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch activity detail:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [owner, timeframe]);

  const calls = details.filter(d => d.action === 'Connect Call');
  const meetings = details.filter(d => d.action === 'Client Visit');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-bold text-slate-800">{owner}</h3>
            <div className="text-xs text-slate-500">{division} &middot; {timeframe}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl leading-none">&times;</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading activity details...</div>
          ) : details.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No activity records found for this timeframe</div>
          ) : (
            <>
              {/* Calls Section */}
              <div>
                <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  Calls <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{calls.length}</span>
                </h4>
                {calls.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No calls recorded</div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Date</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Client</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Contact</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {calls.map(c => (
                          <tr key={c.noteID} className="hover:bg-slate-50 align-top">
                            <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatDateShort(c.dateAdded?.split('T')[0])}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{c.clientName || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{c.contactName || '-'}</td>
                            <td className="px-3 py-2 text-slate-500">{stripHtml(c.comments)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Meetings Section */}
              <div>
                <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                  Client Visits <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{meetings.length}</span>
                </h4>
                {meetings.length === 0 ? (
                  <div className="text-xs text-slate-400 italic">No client visits recorded</div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Date</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Client</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Contact</th>
                          <th className="px-3 py-2 text-left text-slate-500 font-medium">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {meetings.map(m => (
                          <tr key={m.noteID} className="hover:bg-slate-50 align-top">
                            <td className="px-3 py-2 whitespace-nowrap text-slate-600">{formatDateShort(m.dateAdded?.split('T')[0])}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{m.clientName || '-'}</td>
                            <td className="px-3 py-2 text-slate-600">{m.contactName || '-'}</td>
                            <td className="px-3 py-2 text-slate-500">{stripHtml(m.comments)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// KPI View Component
function KPIView({ deals, activityData, kpiGoals, kpiTimeframe, setKpiTimeframe, filterDivision, filterOwner, filterDateFrom, filterDateTo, userDepartments = {} }) {
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Stages that count toward Pipeline Coverage (Qualified through Decision)
  const pipelineStages = ['Qualified', 'Discovery', 'Pitch', 'Negotiation', 'Decision'];

  // Get all owners from deals, activity data, and goals (so users with goals always show)
  const allOwners = [...new Set([
    ...deals.map(d => d.owner),
    ...activityData.map(a => a.owner),
    ...Object.keys(kpiGoals || {})
  ])].filter(Boolean).sort();

  // Filter owners based on filters (using department overrides)
  const filteredOwners = allOwners.filter(owner => {
    if (filterOwner && owner !== filterOwner) return false;
    if (filterDivision) {
      // Check for department override first
      const ownerDivision = userDepartments[owner] ||
        deals.find(d => d.owner === owner)?.division ||
        activityData.find(a => a.owner === owner)?.division;
      if (ownerDivision !== filterDivision) return false;
    }
    return true;
  });

  // Build owner stats from activity data (warehouse) with deal data fallback
  const ownerStats = filteredOwners.map(owner => {
    const activity = activityData.find(a => a.owner === owner) || {};
    const ownerDeals = deals.filter(d => d.owner === owner);
    const division = userDepartments[owner] || activity.division || ownerDeals[0]?.division || '';

    // Calculate Pipeline Coverage using only Qualified-Decision deals with time filter
    const pipelineDeals = ownerDeals.filter(d => {
      // Must be in pipeline stages (Qualified through Decision)
      if (!pipelineStages.includes(d.status)) return false;
      // Apply time filter based on expectedClose
      if (filterDateFrom || filterDateTo) {
        const closeDate = d.expectedClose ? new Date(d.expectedClose) : null;
        if (!closeDate || isNaN(closeDate.getTime())) return false;
        if (filterDateFrom && closeDate < new Date(filterDateFrom)) return false;
        if (filterDateTo && closeDate > new Date(filterDateTo)) return false;
      }
      return true;
    });
    const filteredPipeline = pipelineDeals.reduce((sum, d) => sum + (d.value || 0), 0);

    // Calculate wins/revenue from deals (includes overrides) filtered by timeframe
    const { startDate: tfStart, endDate: tfEnd } = getDateRangeForTimeframe(kpiTimeframe);
    const wonDeals = ownerDeals.filter(d => {
      if (d.status !== 'Closed-Won') return false;
      // Use actualClose if available, fall back to expectedClose for override-based wins
      const closeDate = parseLocalDate(d.actualClose) || parseLocalDate(d.expectedClose);
      if (!closeDate) return false;
      return closeDate >= tfStart && closeDate <= tfEnd;
    });
    const dealsWins = wonDeals.length;
    const dealsRevenue = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);

    // Use the higher of warehouse vs deals-based wins/revenue
    // (warehouse catches actualCloseDate-filtered wins, deals catches override-based wins)
    const wins = Math.max(activity.wins || 0, dealsWins);
    const revenue = Math.max(activity.revenue || 0, dealsRevenue);

    return {
      owner,
      division,
      calls: activity.calls || 0,
      meetings: activity.meetings || 0,
      opportunitiesAdded: activity.opportunitiesAdded || 0,
      wins,
      revenue,
      // Pipeline Coverage uses time-filtered Qualified-Decision deals
      pipeline: filteredPipeline,
    };
  });

  return (
    <div className="space-y-6">
      {/* Header - V4 Layout: Timeframe on left, buttons on right */}
      <div className="flex justify-between items-center">
        {/* Left: Timeframe Toggle */}
        <div className="flex bg-white border border-slate-200 rounded-lg p-1">
          {['Weekly', 'Monthly', 'Quarterly'].map(tf => (
            <button
              key={tf}
              onClick={() => setKpiTimeframe(tf)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                kpiTimeframe === tf ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

      </div>

      {/* KPI Cards grouped by Department */}
      {ownerStats.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No KPI data available for the selected filters
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(
            ownerStats.reduce((groups, stat) => {
              const div = stat.division || 'Unassigned';
              if (!groups[div]) groups[div] = [];
              groups[div].push(stat);
              return groups;
            }, {})
          ).sort(([a], [b]) => a.localeCompare(b)).map(([division, stats]) => (
            <div key={division}>
              <div className="bg-gradient-to-r from-slate-700 to-slate-500 rounded-lg px-5 py-3 mb-4 flex items-center justify-between shadow-sm">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">{division}</h3>
                <span className="text-xs text-slate-300 bg-white/10 px-2.5 py-1 rounded-full">{stats.length} {stats.length === 1 ? 'person' : 'people'}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map(stat => (
                  <KPICard
                    key={stat.owner}
                    owner={stat.owner}
                    division={stat.division}
                    data={stat}
                    goals={kpiGoals[stat.owner]}
                    timeframe={kpiTimeframe}
                    onClick={() => setSelectedOwner(stat)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI Detail Modal */}
      {selectedOwner && (
        <KPIDetailModal
          owner={selectedOwner.owner}
          division={selectedOwner.division}
          timeframe={kpiTimeframe}
          onClose={() => setSelectedOwner(null)}
        />
      )}
    </div>
  );
}

// Admin View Component (Managers only)
// Goals table sub-component (used by TeamView for multiple role contexts)
function GoalsTable({ owners, kpiGoals, onSaveGoal, emptyMessage }) {
  const [editingUsers, setEditingUsers] = useState({});

  const handleGoalChange = (owner, field, value) => {
    setEditingUsers(prev => {
      const existingGoal = kpiGoals[owner] || { calls: 0, meetings: 0, added: 0, won: 0, revenue: 0 };
      return {
        ...prev,
        [owner]: {
          ...(prev[owner] || existingGoal),
          [field]: field === 'revenue' ? (parseFloat(value) || 0) : (parseInt(value, 10) || 0)
        }
      };
    });
  };

  const handleSaveGoals = async (owner) => {
    const userData = editingUsers[owner] || kpiGoals[owner];
    if (userData && onSaveGoal) {
      const success = await onSaveGoal(owner, userData);
      if (success) {
        setEditingUsers(prev => { const next = { ...prev }; delete next[owner]; return next; });
      }
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
          <tr>
            <th className="px-4 py-3 text-left w-48">User</th>
            <th className="px-4 py-3 text-center w-24">Calls</th>
            <th className="px-4 py-3 text-center w-24">Meetings</th>
            <th className="px-4 py-3 text-center w-24">Added</th>
            <th className="px-4 py-3 text-center w-24">Won</th>
            <th className="px-4 py-3 text-center w-28">Revenue</th>
            <th className="px-4 py-3 text-center w-24"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {owners.map(owner => {
            const existingGoal = kpiGoals[owner] || {};
            const goalData = editingUsers[owner] || {
              calls: existingGoal.calls || 0,
              meetings: existingGoal.meetings || 0,
              added: existingGoal.added || 0,
              won: existingGoal.won || 0,
              revenue: existingGoal.revenue || 0
            };
            const hasChanges = editingUsers[owner] !== undefined;
            return (
              <tr key={owner} className={hasChanges ? 'bg-blue-50' : ''}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{owner}</div>
                  {kpiGoals[owner] && <div className="text-[10px] text-green-600">Goals set</div>}
                </td>
                <td className="px-4 py-3 text-center">
                  <input type="number" value={goalData.calls} onChange={e => handleGoalChange(owner, 'calls', e.target.value)}
                    className="w-16 px-2 py-1.5 border border-slate-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500" />
                </td>
                <td className="px-4 py-3 text-center">
                  <input type="number" value={goalData.meetings} onChange={e => handleGoalChange(owner, 'meetings', e.target.value)}
                    className="w-16 px-2 py-1.5 border border-slate-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500" />
                </td>
                <td className="px-4 py-3 text-center">
                  <input type="number" value={goalData.added} onChange={e => handleGoalChange(owner, 'added', e.target.value)}
                    className="w-16 px-2 py-1.5 border border-slate-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500" />
                </td>
                <td className="px-4 py-3 text-center">
                  <input type="number" value={goalData.won} onChange={e => handleGoalChange(owner, 'won', e.target.value)}
                    className="w-16 px-2 py-1.5 border border-slate-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500" />
                </td>
                <td className="px-4 py-3 text-center">
                  <input type="number" value={goalData.revenue} onChange={e => handleGoalChange(owner, 'revenue', e.target.value)}
                    className="w-20 px-2 py-1.5 border border-slate-300 rounded text-center text-sm focus:ring-2 focus:ring-blue-500" placeholder="0" />
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => handleSaveGoals(owner)} disabled={!hasChanges}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${hasChanges ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
                    Save
                  </button>
                </td>
              </tr>
            );
          })}
          {owners.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-slate-400">{emptyMessage || 'No users found.'}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Department Head: Teams management sub-view
function DeptTeamsManager({ department, userEmail, userDepartments, allOwners, kpiGoals, onSaveGoal }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState({});
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [teamSubTab, setTeamSubTab] = useState('teams');
  const [groupMembers, setGroupMembers] = useState([]);
  const [extraDeptMembers, setExtraDeptMembers] = useState([]); // AD users added to dept but not in warehouse

  const warehouseDeptOwners = allOwners.filter(o => userDepartments[o] === department);
  // Merge warehouse dept owners + any AD users manually added to this dept
  const deptOwners = [...new Set([...warehouseDeptOwners, ...extraDeptMembers])].sort();

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const url = department ? `/api/teams?department=${encodeURIComponent(department)}` : '/api/teams';
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) setTeams(data.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchGroupMembers = async () => {
    try {
      const res = await fetch('/api/managers/group-members');
      const data = await res.json();
      if (data.success) setGroupMembers(data.data);
    } catch (e) { console.error(e); }
  };

  const fetchEligibleManagers = async (owners) => {
    const list = owners || deptOwners;
    if (!list.length) { setEligibleManagers([]); return; }
    setLoadingEligible(true);
    try {
      const res = await fetch('/api/teams/eligible-managers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owners: list }),
      });
      const data = await res.json();
      if (data.success) setEligibleManagers(data.data);
    } catch (e) { console.error(e); }
    setLoadingEligible(false);
  };

  useEffect(() => {
    if (department) {
      fetchTeams();
      fetchGroupMembers();
      fetchEligibleManagers();
    }
  }, [department]);

  // AD members not already in deptOwners (by display name)
  const deptOwnerSet = new Set(deptOwners.map(o => o.toLowerCase()));
  const addableFromGroup = groupMembers.filter(m => !deptOwnerSet.has(m.displayName.toLowerCase()));

  const handleAddFromGroup = async (displayName) => {
    // Assign this person to the department so they appear in deptOwners
    try {
      await fetch('/api/user-departments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner: displayName, department }),
      });
      const updated = [...new Set([...extraDeptMembers, displayName])];
      setExtraDeptMembers(updated);
      fetchEligibleManagers([...deptOwners, displayName]);
    } catch (e) { console.error(e); }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    setCreatingTeam(true);
    try {
      const res = await fetch(`/api/teams?department=${encodeURIComponent(department)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTeamName.trim() }),
      });
      const data = await res.json();
      if (data.success) { setTeams(data.data); setNewTeamName(''); }
      else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
    setCreatingTeam(false);
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team? Members will be unassigned.')) return;
    try {
      const res = await fetch(`/api/teams/${teamId}?department=${encodeURIComponent(department)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) setTeams(data.data);
      else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
  };

  const handleUpdateTeam = async (team) => {
    const edit = editingTeam[team.id] || {};
    const name = edit.name !== undefined ? edit.name : team.name;
    const managerEmail = edit.managerEmail !== undefined ? edit.managerEmail : team.managerEmail;
    try {
      const res = await fetch(`/api/teams/${team.id}?department=${encodeURIComponent(department)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, managerEmail: managerEmail || null }),
      });
      const data = await res.json();
      if (data.success) {
        setTeams(data.data);
        setEditingTeam(prev => { const n = { ...prev }; delete n[team.id]; return n; });
      } else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
  };

  const handleSetMembers = async (teamId, members) => {
    try {
      const res = await fetch(`/api/teams/${teamId}/members?department=${encodeURIComponent(department)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ members }),
      });
      const data = await res.json();
      if (data.success) setTeams(data.data);
      else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
  };

  const handleAddTeamManager = async (email, displayName) => {
    try {
      const res = await fetch(`/api/teams/add-team-manager?department=${encodeURIComponent(department)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, displayName }),
      });
      const data = await res.json();
      if (!data.success) alert(`Failed: ${data.error}`);
      else fetchEligibleManagers(); // Refresh eligible list
    } catch (e) { alert(e.message); }
  };

  // All owners currently assigned to any team in this dept
  const assignedOwners = new Set(teams.flatMap(t => t.members));
  // Build combined list of all potential team managers (email -> displayName)
  const managerOptionsMap = {};
  // Already assigned team managers
  teams.filter(t => t.managerEmail).forEach(t => {
    managerOptionsMap[t.managerEmail] = t.managerDisplayName || t.managerEmail;
  });
  // Eligible managers from AD group
  eligibleManagers.forEach(m => {
    managerOptionsMap[m.email] = m.displayName || m.email;
  });
  // Department head (current user if they're the dept head)
  if (userEmail && !managerOptionsMap[userEmail]) {
    managerOptionsMap[userEmail] = userEmail;
  }
  const managerOptions = Object.entries(managerOptionsMap).sort((a, b) => a[1].localeCompare(b[1]));

  const goalsOwners = [...new Set([
    ...deptOwners,
    ...Object.keys(kpiGoals || {}).filter(o => userDepartments[o] === department),
  ])].sort();

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {['teams', 'goals'].map(tab => (
          <button key={tab} onClick={() => setTeamSubTab(tab)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${teamSubTab === tab ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {teamSubTab === 'goals' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-slate-50">
            <h3 className="font-bold text-slate-700">Department Goals</h3>
            <p className="text-sm text-slate-500 mt-1">Set quarterly targets for your department members</p>
          </div>
          {goalsOwners.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">No department members found. Ensure users are assigned to your department in Admin.</div>
          ) : (
            <GoalsTable owners={goalsOwners} kpiGoals={kpiGoals} onSaveGoal={onSaveGoal} />
          )}
        </div>
      )}

      {teamSubTab === 'teams' && (
        <div className="space-y-4">
          {/* Add dept member from Group */}
          {addableFromGroup.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Add member from group:</span>
              <AddFromGroupButton
                groupMembers={addableFromGroup}
                existingOwners={deptOwners}
                onAdd={handleAddFromGroup}
              />
            </div>
          )}
          {/* Create team */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateTeam()}
              placeholder="New team name..."
              className="flex-1 max-w-xs px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button onClick={handleCreateTeam} disabled={creatingTeam || !newTeamName.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {creatingTeam ? 'Creating...' : 'Create Team'}
            </button>
          </div>

          {/* Teams list */}
          {loading ? (
            <div className="text-slate-400 text-sm py-4">Loading teams...</div>
          ) : teams.length === 0 ? (
            <div className="text-slate-400 text-sm py-4 italic">No teams yet. Create one above.</div>
          ) : (
            <div className="space-y-2">
              {teams.map(team => {
                const isExpanded = expandedTeam === team.id;
                const edit = editingTeam[team.id] || {};
                const currentName = edit.name !== undefined ? edit.name : team.name;
                const currentManager = edit.managerEmail !== undefined ? edit.managerEmail : (team.managerEmail || '');
                const hasEdits = Object.keys(edit).length > 0;
                const teamMemberSet = new Set(team.members);

                return (
                  <div key={team.id} className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button className="flex-1 flex items-center gap-3 text-left" onClick={() => setExpandedTeam(isExpanded ? null : team.id)}>
                        <span className="font-medium text-slate-800">{team.name}</span>
                        {team.managerDisplayName && <span className="text-xs text-slate-500">Manager: {team.managerDisplayName}</span>}
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{team.members.length} members</span>
                        <span className="text-slate-300">{isExpanded ? '▲' : '▼'}</span>
                      </button>
                      <button onClick={() => handleDeleteTeam(team.id)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 border border-red-200 rounded hover:bg-red-50 transition-colors">
                        Delete
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-slate-100 px-4 py-4 bg-slate-50 space-y-4">
                        {/* Edit name + manager */}
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Team Name</label>
                            <input type="text" value={currentName}
                              onChange={e => setEditingTeam(prev => ({ ...prev, [team.id]: { ...prev[team.id], name: e.target.value } }))}
                              className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 w-48" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Team Manager</label>
                            <select value={currentManager}
                              onChange={e => setEditingTeam(prev => ({ ...prev, [team.id]: { ...prev[team.id], managerEmail: e.target.value } }))}
                              className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 w-56">
                              <option value="">— None —</option>
                              {managerOptions.map(([email, name]) => (
                                <option key={email} value={email}>{name}</option>
                              ))}
                            </select>
                          </div>
                          {hasEdits && (
                            <button onClick={() => handleUpdateTeam(team)}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors">
                              Save Changes
                            </button>
                          )}
                        </div>

                        {/* Members */}
                        <div>
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2">Members ({team.members.length})</div>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 max-h-40 overflow-y-auto mb-2">
                            {deptOwners.map(owner => (
                              <label key={owner} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                <input type="checkbox"
                                  checked={teamMemberSet.has(owner)}
                                  onChange={e => {
                                    const newMembers = e.target.checked
                                      ? [...team.members, owner]
                                      : team.members.filter(m => m !== owner);
                                    handleSetMembers(team.id, newMembers);
                                  }}
                                  className="rounded" />
                                {owner}
                              </label>
                            ))}
                          </div>
                          {deptOwners.length === 0 && (
                            <p className="text-xs text-slate-400 italic">No department members found. Assign users to this department in Admin.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Eligible managers to promote */}
          {eligibleManagers.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-2">Promote to Team Manager</h4>
              <p className="text-xs text-slate-500 mb-2">Department members with matching AD accounts who can be assigned as team managers.</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Email</th>
                      <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {eligibleManagers.map(m => (
                      <tr key={m.email} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium text-slate-800">{m.displayName}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{m.email}</td>
                        <td className="px-4 py-2 text-right">
                          <button onClick={() => handleAddTeamManager(m.email, m.displayName)}
                            className="text-xs bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700 transition-colors">
                            Add as Team Manager
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamView({ deals, activityData, kpiGoals, userDepartments, onSaveGoal, availableDivisions, userEmail, role }) {
  const [myTeam, setMyTeam] = useState(null); // for team_manager
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [allManagers, setAllManagers] = useState([]);

  const allOwners = [...new Set([
    ...deals.map(d => d.owner),
    ...activityData.map(a => a.owner),
    ...Object.keys(kpiGoals || {}),
  ])].filter(Boolean).sort();

  // Admin: load managers to show dept head info
  useEffect(() => {
    if (role !== 'admin') return;
    fetch('/api/managers').then(r => r.json()).then(data => { if (data.success) setAllManagers(data.data); }).catch(() => {});
  }, [role]);

  // Team manager: load their team
  useEffect(() => {
    if (role !== 'team_manager') return;
    setLoadingTeam(true);
    fetch('/api/teams/my-team')
      .then(r => r.json())
      .then(data => { setMyTeam(data.success ? data.data : null); setLoadingTeam(false); })
      .catch(() => { setMyTeam(null); setLoadingTeam(false); });
  }, [role]);

  const [deptHeadDept, setDeptHeadDept] = useState('');

  useEffect(() => {
    if (role !== 'department_head') return;
    fetch('/api/auth/me')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data.department) setDeptHeadDept(data.data.department);
      })
      .catch(() => {});
  }, [role]);

  // Team manager view
  if (role === 'team_manager') {
    if (loadingTeam) return <div className="p-8 text-center text-slate-400">Loading your team...</div>;
    const teamOwners = myTeam ? myTeam.members : [];
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800">My Team — {myTeam?.name || 'Goals'}</h2>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-slate-50">
            <h3 className="font-bold text-slate-700">Quarterly Goals</h3>
          </div>
          <GoalsTable
            owners={teamOwners}
            kpiGoals={kpiGoals}
            onSaveGoal={onSaveGoal}
            emptyMessage="No team members assigned. Ask your department head to assign members to your team."
          />
        </div>
      </div>
    );
  }

  // Department head view
  if (role === 'department_head') {
    if (!deptHeadDept) return <div className="p-8 text-center text-slate-400">Loading department info...</div>;
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-800">Team — {deptHeadDept}</h2>
        </div>
        <DeptTeamsManager
          department={deptHeadDept}
          userEmail={userEmail}
          userDepartments={userDepartments}
          allOwners={allOwners}
          kpiGoals={kpiGoals}
          onSaveGoal={onSaveGoal}
        />
      </div>
    );
  }

  // Admin view: department picker
  const [adminDept, setAdminDept] = React.useState('');
  const depts = availableDivisions.length > 0 ? availableDivisions : [...new Set(Object.values(userDepartments || {}))].filter(Boolean).sort();
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <h2 className="text-xl font-bold text-slate-800">Team</h2>
        <select value={adminDept} onChange={e => setAdminDept(e.target.value)}
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
          <option value="">Select Department...</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      {adminDept && (() => {
        const head = allManagers.find(m => m.managerType === 'department_head' && m.department === adminDept);
        return head ? (
          <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <span className="text-xs text-slate-500 uppercase tracking-wide font-medium">Department Head</span>
            <span className="font-semibold text-slate-800">{head.displayName}</span>
            <span className="text-slate-400 text-sm">{head.userEmail}</span>
          </div>
        ) : null;
      })()}
      {adminDept ? (
        <DeptTeamsManager
          department={adminDept}
          userEmail={userEmail}
          userDepartments={userDepartments}
          allOwners={allOwners}
          kpiGoals={kpiGoals}
          onSaveGoal={onSaveGoal}
        />
      ) : (
        <div className="text-slate-400 text-sm p-8 text-center">Select a department to manage teams.</div>
      )}
    </div>
  );
}

function AddDeptHeadButton({ groupMembers, loading, availableDivisions, onAdd, adding }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // { email, displayName }
  const [dept, setDept] = useState('');
  const [search, setSearch] = useState('');

  const handleAdd = async () => {
    if (!selected || !dept) return;
    await onAdd(selected.email, selected.displayName, dept);
    setOpen(false);
    setSelected(null);
    setDept('');
    setSearch('');
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)} disabled={loading}
        className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors">
        + Add from Group
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-10 bg-white border border-slate-200 rounded-lg shadow-lg w-80 p-3 space-y-2">
          <div className="text-xs font-bold text-slate-600 uppercase mb-1">Add Department Head</div>
          {groupMembers.length === 0 ? (
            <p className="text-xs text-slate-400">No available group members.</p>
          ) : (
            <>
              <input
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setSelected(null); }}
                placeholder="Search name..."
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              {search && (
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded bg-white">
                  {groupMembers.filter(m => m.displayName.toLowerCase().includes(search.toLowerCase())).map(m => (
                    <button key={m.email} onClick={() => { setSelected(m); setSearch(m.displayName); }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 ${selected?.email === m.email ? 'bg-blue-50 text-blue-700' : ''}`}>
                      {m.displayName}
                    </button>
                  ))}
                  {groupMembers.filter(m => m.displayName.toLowerCase().includes(search.toLowerCase())).length === 0 && (
                    <p className="px-3 py-2 text-xs text-slate-400">No matches</p>
                  )}
                </div>
              )}
              <select value={dept} onChange={e => setDept(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500">
                <option value="">Select department...</option>
                {availableDivisions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div className="flex gap-2 pt-1">
                <button onClick={handleAdd} disabled={!selected || !dept || !!adding}
                  className="flex-1 text-xs bg-emerald-600 text-white px-3 py-1.5 rounded hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                  {adding ? 'Adding...' : 'Add as Dept Head'}
                </button>
                <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AddFromGroupButton({ groupMembers, existingOwners, onAdd }) {
  const [open, setOpen] = useState(false);
  const existingSet = new Set(existingOwners.map(o => o.toLowerCase()));
  const addable = groupMembers.filter(m => !existingSet.has(m.displayName.toLowerCase()));

  if (addable.length === 0) return null;

  return (
    <div className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 transition-colors">
        + Add from Group
      </button>
      {open && (
        <div className="absolute right-0 top-8 z-10 bg-white border border-slate-200 rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto">
          {addable.map(m => (
            <button key={m.email} onClick={() => { onAdd(m.displayName); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
              <div className="font-medium text-slate-800">{m.displayName}</div>
              <div className="text-xs text-slate-400">{m.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Admin View Component (Admins only - manage dept heads + user department assignments)
function AdminView({ availableDivisions, allActivityOwners, userDepartments, onSaveDepartment }) {
  const [managers, setManagers] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [addForm, setAddForm] = useState({ email: '', displayName: '', department: '' }); // manual add form
  const [addingDept, setAddingDept] = useState(null); // email being added via group
  const [savingDept, setSavingDept] = useState({}); // owner -> bool

  const fetchManagers = async () => {
    setLoadingManagers(true);
    try {
      const res = await fetch('/api/managers');
      const data = await res.json();
      if (data.success) setManagers(data.data);
    } catch (err) { console.error(err); }
    setLoadingManagers(false);
  };

  const fetchGroupMembers = async () => {
    setLoadingGroup(true);
    try {
      const res = await fetch('/api/managers/group-members');
      const data = await res.json();
      if (data.success) setGroupMembers(data.data);
    } catch (err) { console.error(err); }
    setLoadingGroup(false);
  };

  useEffect(() => {
    fetchManagers();
    fetchGroupMembers();
  }, []);

  const deptHeads = managers.filter(m => m.managerType === 'department_head');
  const existingDeptHeadEmails = new Set(deptHeads.map(m => m.userEmail.toLowerCase()));
  const addableFromGroup = groupMembers.filter(gm => !existingDeptHeadEmails.has(gm.email.toLowerCase()));

  const handleAddDeptHeadFromGroup = async (email, displayName, department) => {
    if (!department) { alert('Please select a department first.'); return; }
    setAddingDept(email);
    try {
      const res = await fetch('/api/managers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, displayName, department }),
      });
      const data = await res.json();
      if (data.success) setManagers(data.data);
      else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
    setAddingDept(null);
  };

  const handleRemoveDeptHead = async (email) => {
    if (!confirm(`Remove ${email} as department head?`)) return;
    try {
      const res = await fetch(`/api/managers/${encodeURIComponent(email)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) setManagers(data.data);
      else alert(`Failed: ${data.error}`);
    } catch (e) { alert(e.message); }
  };

  const handleSaveDept = async (owner, dept) => {
    setSavingDept(prev => ({ ...prev, [owner]: true }));
    await onSaveDepartment(owner, dept);
    setSavingDept(prev => ({ ...prev, [owner]: false }));
  };

  // Group members who need a dept picker before adding
  const [groupDeptPick, setGroupDeptPick] = useState({}); // email -> selected dept

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-6 space-y-8">
      <h2 className="text-xl font-bold text-slate-800">Admin</h2>

      {/* Department Heads */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide">Department Heads</h3>
          <AddDeptHeadButton
            groupMembers={addableFromGroup}
            loading={loadingGroup}
            availableDivisions={availableDivisions}
            onAdd={handleAddDeptHeadFromGroup}
            adding={addingDept}
          />
        </div>
        {loadingManagers ? (
          <div className="text-slate-400 text-sm py-4">Loading...</div>
        ) : deptHeads.length === 0 ? (
          <div className="text-slate-400 text-sm py-4 italic">No department heads configured yet. Use the button above to add one.</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Department</th>
                  <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {deptHeads.map(mgr => (
                  <tr key={mgr.userEmail} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-800">{mgr.displayName}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{mgr.userEmail}</td>
                    <td className="px-4 py-2 text-slate-600">{mgr.department}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => handleRemoveDeptHead(mgr.userEmail)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 border border-red-200 rounded hover:bg-red-50 transition-colors">
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Department Assignments */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide">User Department Assignments</h3>
          <AddFromGroupButton groupMembers={groupMembers} existingOwners={allActivityOwners} onAdd={name => onSaveDepartment(name, '')} />
        </div>
        <p className="text-xs text-slate-500 mb-3">Assign people to departments. This determines which department head oversees each person.</p>
        {allActivityOwners.length === 0 ? (
          <div className="text-slate-400 text-sm py-4 italic">No users found. Activity data may still be loading.</div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">User</th>
                    <th className="px-4 py-2 text-left text-xs font-bold text-slate-500 uppercase">Department</th>
                    <th className="px-4 py-2 text-right text-xs font-bold text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {allActivityOwners.map(owner => (
                    <UserDeptRow
                      key={owner}
                      owner={owner}
                      current={userDepartments[owner] || ''}
                      divisions={availableDivisions}
                      onSave={handleSaveDept}
                      saving={savingDept[owner] || false}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UserDeptRow({ owner, current, divisions, onSave, saving }) {
  const [selected, setSelected] = useState(current);

  // Sync when parent data loads after initial render
  useEffect(() => {
    setSelected(current);
  }, [current]);

  const hasChange = selected !== current;
  return (
    <tr className={hasChange ? 'bg-blue-50' : 'hover:bg-slate-50'}>
      <td className="px-4 py-2 font-medium text-slate-800">{owner}</td>
      <td className="px-4 py-2">
        <select value={selected} onChange={e => setSelected(e.target.value)}
          className="px-2 py-1 border border-slate-300 rounded text-xs focus:ring-2 focus:ring-blue-500 w-44">
          <option value="">— Unassigned —</option>
          {divisions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={() => onSave(owner, selected)} disabled={!hasChange || saving}
          className={`text-xs px-3 py-1 rounded transition-colors ${hasChange && !saving ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </td>
    </tr>
  );
}

// Meetings View Component
function MeetingsView({ deals, noteMeetings = [], filterDivision, filterOwner, filterStages, userDepartments = {} }) {
  const [selectedPeriod, setSelectedPeriod] = useState('thisWeek');

  // Helper to get effective division (considering department overrides)
  const getEffectiveDivision = (deal) => userDepartments[deal.owner] || deal.division;

  const today = new Date();

  // Get week ranges
  const getWeekRange = (offset) => {
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    const start = new Date(today.getFullYear(), today.getMonth(), diff);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // Get month ranges
  const getMonthRange = (offset) => {
    const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // Get quarter range
  const getQuarterRange = (offset) => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const targetQuarter = currentQuarter + offset;
    const year = today.getFullYear() + Math.floor(targetQuarter / 4);
    const quarter = ((targetQuarter % 4) + 4) % 4;
    const start = new Date(year, quarter * 3, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(year, quarter * 3 + 3, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // Define all date ranges
  const dateRanges = {
    lastWeek: getWeekRange(-1),
    thisWeek: getWeekRange(0),
    nextWeek: getWeekRange(1),
    next2Weeks: { start: getWeekRange(1).start, end: getWeekRange(2).end },
    thisMonth: getMonthRange(0),
    nextMonth: getMonthRange(1),
    thisQuarter: getQuarterRange(0),
    nextQuarter: getQuarterRange(1),
    all: { start: new Date(2000, 0, 1), end: new Date(2100, 11, 31) }
  };

  // Extract meetings from deals
  const allMeetings = [];
  deals.forEach(deal => {
    // Apply filters (using effective division with overrides)
    if (filterDivision && getEffectiveDivision(deal) !== filterDivision) return;
    if (filterOwner && deal.owner !== filterOwner) return;
    if (filterStages.length > 0 && !filterStages.includes(deal.status)) return;

    const addMeeting = (dateStr, type) => {
      const d = parseLocalDate(dateStr);
      if (d) {
        allMeetings.push({
          id: deal.id + type,
          dealTitle: deal.title,
          client: deal.title.split('-')[0].trim(),
          owner: deal.owner,
          division: deal.division || 'Unassigned',
          value: deal.value || 0,
          date: d,
          type,
          dealStatus: deal.status
        });
      }
    };

    addMeeting(deal.lastAppt, 'Last Appointment');
    addMeeting(deal.nextAppt, 'Next Appointment');
  });

  // Add note-based Client Visit meetings from warehouse
  noteMeetings.forEach(nm => {
    const owner = nm.owner;
    const division = userDepartments[owner] || nm.division || 'Unassigned';
    if (filterDivision && division !== filterDivision) return;
    if (filterOwner && owner !== filterOwner) return;
    const d = parseLocalDate(nm.dateAdded?.split('T')[0]);
    if (d) {
      allMeetings.push({
        id: 'note-' + nm.noteID,
        dealTitle: nm.contactName || 'Client Visit',
        client: nm.clientName || 'Unknown',
        owner,
        division,
        value: 0,
        date: d,
        type: 'Client Visit',
        dealStatus: null
      });
    }
  });

  // Count meetings in range
  const countInRange = (range) => {
    return allMeetings.filter(m => m.date >= range.start && m.date <= range.end).length;
  };

  // Filter meetings based on selected period
  const selectedRange = dateRanges[selectedPeriod] || dateRanges.thisWeek;
  const filteredMeetings = allMeetings
    .filter(m => m.date >= selectedRange.start && m.date <= selectedRange.end)
    .sort((a, b) => a.date - b.date);

  // Group by Division then Owner
  const groupedMeetings = {};
  filteredMeetings.forEach(meeting => {
    if (!groupedMeetings[meeting.division]) {
      groupedMeetings[meeting.division] = {};
    }
    if (!groupedMeetings[meeting.division][meeting.owner]) {
      groupedMeetings[meeting.division][meeting.owner] = [];
    }
    groupedMeetings[meeting.division][meeting.owner].push(meeting);
  });

  // Format date range for display
  const formatRangeLabel = (range) => {
    return `${range.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${range.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  // Period options organized in groups
  const periodOptions = [
    { key: 'lastWeek', label: 'Last Week', group: 'weeks' },
    { key: 'thisWeek', label: 'This Week', group: 'weeks' },
    { key: 'nextWeek', label: 'Next Week', group: 'weeks' },
    { key: 'next2Weeks', label: 'Next 2 Weeks', group: 'weeks' },
    { key: 'thisMonth', label: 'This Month', group: 'months' },
    { key: 'nextMonth', label: 'Next Month', group: 'months' },
    { key: 'thisQuarter', label: 'This Quarter', group: 'quarters' },
    { key: 'nextQuarter', label: 'Next Quarter', group: 'quarters' },
    { key: 'all', label: 'All Time', group: 'all' },
  ];

  return (
    <div className="space-y-8">
      {/* Summary Cards - V4 Style */}
      <div className="grid grid-cols-3 gap-6">
        <button
          onClick={() => setSelectedPeriod('lastWeek')}
          className={`p-6 rounded-xl border shadow-sm text-center transition-all ${
            selectedPeriod === 'lastWeek'
              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-offset-2'
              : 'bg-white border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Last Week</div>
          <div className="text-4xl font-bold text-slate-700">{countInRange(dateRanges.lastWeek)}</div>
          <div className="text-xs text-slate-400 mt-1">Meetings Completed</div>
        </button>
        <button
          onClick={() => setSelectedPeriod('thisWeek')}
          className={`p-6 rounded-xl border shadow-sm text-center transition-all ${
            selectedPeriod === 'thisWeek'
              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-offset-2'
              : 'bg-white border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">This Week</div>
          <div className="text-4xl font-bold text-blue-700">{countInRange(dateRanges.thisWeek)}</div>
          <div className="text-xs text-blue-400 mt-1">Scheduled / Completed</div>
        </button>
        <button
          onClick={() => setSelectedPeriod('nextWeek')}
          className={`p-6 rounded-xl border shadow-sm text-center transition-all ${
            selectedPeriod === 'nextWeek'
              ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-offset-2'
              : 'bg-white border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Next Week</div>
          <div className="text-4xl font-bold text-slate-700">{countInRange(dateRanges.nextWeek)}</div>
          <div className="text-xs text-slate-400 mt-1">Upcoming</div>
        </button>
      </div>

      {/* Additional Period Filters */}
      <div className="flex flex-wrap gap-2">
        {periodOptions.filter(p => !['lastWeek', 'thisWeek', 'nextWeek'].includes(p.key)).map(period => (
          <button
            key={period.key}
            onClick={() => setSelectedPeriod(period.key)}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
              selectedPeriod === period.key
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
            }`}
          >
            {period.label}
            <span className="ml-2 text-xs opacity-70">({countInRange(dateRanges[period.key])})</span>
          </button>
        ))}
      </div>

      {/* Full Meeting Agenda - V4 Style */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon />
            {selectedPeriod === 'all' ? 'Full Meeting Agenda' : `${periodOptions.find(p => p.key === selectedPeriod)?.label || 'Meeting'} Agenda`}
          </h3>
          <span className="text-xs text-gray-500">Grouped by Department &gt; Owner</span>
        </div>
        <div className="p-4 space-y-6">
          {Object.keys(groupedMeetings).length === 0 ? (
            <div className="p-8 text-center text-gray-400 italic">No meeting data found.</div>
          ) : Object.keys(groupedMeetings).sort().map(division => (
            <div key={division} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                <span className="font-bold text-slate-800 flex items-center gap-2">
                  <BriefcaseIcon />
                  {division}
                </span>
                <span className="text-xs font-bold bg-white px-2 py-1 rounded text-slate-600 border border-slate-200">
                  {Object.values(groupedMeetings[division]).flat().length} Meetings
                </span>
              </div>

              <div className="divide-y divide-slate-100">
                {Object.keys(groupedMeetings[division]).sort().map(owner => (
                  <div key={owner} className="bg-white">
                    <div className="px-4 py-2 bg-slate-50/50 flex justify-between items-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      <span>{owner}</span>
                      <span>{groupedMeetings[division][owner].length}</span>
                    </div>
                    <table className="w-full text-sm text-left">
                      <tbody className="divide-y divide-slate-50">
                        {groupedMeetings[division][owner].map(meeting => (
                          <tr key={meeting.id} className="hover:bg-blue-50/50">
                            <td className="px-4 py-2 w-32 text-slate-600 border-r border-slate-100">
                              {meeting.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </td>
                            <td className="px-4 py-2">
                              <div className="font-medium text-slate-900">{meeting.client}</div>
                              <div className="text-xs text-slate-400">{meeting.dealTitle}</div>
                            </td>
                            <td className="px-4 py-2 w-40">
                              <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide ${
                                meeting.type === 'Next Appointment' ? 'bg-blue-100 text-blue-700' : meeting.type === 'Client Visit' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {meeting.type}
                              </span>
                            </td>
                            <td className="px-4 py-2 w-32 text-right font-mono text-slate-600">
                              {meeting.value ? formatMoney(meeting.value) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Briefcase icon
const BriefcaseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);

// Download icon for export
const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/>
  </svg>
);

// Printer icon
const PrinterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/>
  </svg>
);

// Upload icon
const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/>
  </svg>
);

// Save icon
const SaveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);

// Trending up icon
const TrendingUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
);

// Trending down icon
const TrendingDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>
  </svg>
);

// Executive Summary View Component
function ExecSummaryView({ deals, funnelData, totalPipelineValue, totalWeightedValue }) {
  const closedWonDeals = deals.filter(d => d.status === 'Closed-Won');
  const activeDeals = deals.filter(d => d.status !== 'Closed-Lost' && d.status !== 'Closed-Won');

  // Date helpers
  const today = new Date();
  const getWeekRange = (offset) => {
    const d = new Date(today);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    const start = new Date(d.setDate(diff));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const getQuarterRange = () => {
    const q = Math.floor((today.getMonth() + 3) / 3);
    const start = new Date(today.getFullYear(), (q - 1) * 3, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today.getFullYear(), q * 3, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const checkDateInRange = (dateStr, range) => {
    if (!dateStr) return false;
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return false;
    return d >= range.start && d <= range.end;
  };

  const thisWeek = getWeekRange(0);
  const lastWeek = getWeekRange(-1);
  const thisQuarter = getQuarterRange();

  const wonThisWeek = closedWonDeals.filter(d => checkDateInRange(d.actualClose, thisWeek));
  const wonLastWeek = closedWonDeals.filter(d => checkDateInRange(d.actualClose, lastWeek));
  const wonThisQuarter = closedWonDeals.filter(d => checkDateInRange(d.actualClose, thisQuarter));

  const wonThisWeekValue = wonThisWeek.reduce((s, d) => s + d.value, 0);
  const wonLastWeekValue = wonLastWeek.reduce((s, d) => s + d.value, 0);
  const wonThisQuarterValue = wonThisQuarter.reduce((s, d) => s + d.value, 0);

  // Late stage (70%+)
  const lateStageDeals = activeDeals.filter(d => d.probability >= 70);
  const lateStageValue = lateStageDeals.reduce((sum, d) => sum + d.value, 0);

  // Weighted pipeline value
  const weightedPipelineValue = activeDeals.reduce((sum, d) => sum + (d.value * (d.probability / 100)), 0);

  // Service line stats
  const divisionMap = {};
  closedWonDeals.forEach(d => {
    const division = d.division || 'Other';
    if (!divisionMap[division]) divisionMap[division] = { revenue: 0, count: 0 };
    divisionMap[division].revenue += d.value;
    divisionMap[division].count += 1;
  });
  const serviceLineStats = Object.entries(divisionMap).map(([name, stats]) => ({ name, ...stats })).sort((a, b) => b.revenue - a.revenue);

  // Group deals by stage for forecast
  const groupedDeals = {
    'Decision': activeDeals.filter(d => d.status === 'Decision').sort((a, b) => new Date(a.expectedClose) - new Date(b.expectedClose)),
    'Negotiation': activeDeals.filter(d => d.status === 'Negotiation').sort((a, b) => new Date(a.expectedClose) - new Date(b.expectedClose)),
    'Pitch': activeDeals.filter(d => d.status === 'Pitch').sort((a, b) => new Date(a.expectedClose) - new Date(b.expectedClose)),
    'Discovery': activeDeals.filter(d => d.status === 'Discovery').sort((a, b) => new Date(a.expectedClose) - new Date(b.expectedClose)),
    'Qualified': activeDeals.filter(d => d.status === 'Qualified').sort((a, b) => new Date(a.expectedClose) - new Date(b.expectedClose)),
  };

  // Top performers
  const ownerMap = {};
  closedWonDeals.forEach(d => {
    const owner = d.owner || 'Unknown';
    if (!ownerMap[owner]) ownerMap[owner] = { name: owner, revenue: 0, count: 0 };
    ownerMap[owner].revenue += d.value;
    ownerMap[owner].count += 1;
  });
  const top3Revenue = Object.values(ownerMap).sort((a, b) => b.revenue - a.revenue).slice(0, 3);
  const top3Volume = Object.values(ownerMap).sort((a, b) => b.count - a.count).slice(0, 3);

  const recentWins = [...wonThisWeek, ...wonLastWeek].slice(0, 5);

  // Export function - opens clean version in new window
  const handleExport = () => {
    const exportWindow = window.open('', '_blank');
    if (!exportWindow) {
      alert('Please allow popups to export the report');
      return;
    }

    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>GHR Sales Report - ${today.toLocaleDateString()}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1e293b; padding: 40px; max-width: 1000px; margin: 0 auto; }
    h1 { font-size: 24px; font-weight: 700; margin-bottom: 4px; }
    h2 { font-size: 16px; font-weight: 600; color: #475569; margin: 24px 0 12px 0; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; }
    h3 { font-size: 14px; font-weight: 600; background: #f1f5f9; padding: 8px 12px; margin: 16px 0 0 0; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 16px; margin-bottom: 24px; }
    .date { font-size: 12px; color: #64748b; }
    .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
    .kpi { background: #f8fafc; border: 1px solid #e2e8f0; padding: 16px; border-radius: 8px; }
    .kpi-label { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 600; letter-spacing: 0.5px; }
    .kpi-value { font-size: 24px; font-weight: 700; margin-top: 4px; }
    .kpi-value.green { color: #059669; }
    .kpi-value.blue { color: #2563eb; }
    .kpi-value.purple { color: #7c3aed; }
    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { text-align: left; padding: 8px; background: #f1f5f9; border-bottom: 1px solid #e2e8f0; font-size: 10px; text-transform: uppercase; color: #64748b; }
    td { padding: 8px; border-bottom: 1px solid #f1f5f9; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: 600; }
    .text-green { color: #059669; }
    .text-blue { color: #2563eb; }
    .wins-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; text-align: center; background: #f8fafc; padding: 12px; border-radius: 8px; margin-bottom: 16px; }
    .wins-summary div { border-right: 1px solid #e2e8f0; }
    .wins-summary div:last-child { border-right: none; }
    .wins-label { font-size: 10px; text-transform: uppercase; color: #059669; font-weight: 600; }
    .wins-value { font-size: 18px; font-weight: 700; }
    .funnel-row { display: grid; grid-template-columns: 120px 1fr 100px; gap: 16px; align-items: center; margin-bottom: 8px; }
    .funnel-bar { height: 28px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
    .funnel-fill { height: 100%; display: flex; align-items: center; justify-content: center; color: white; font-weight: 600; font-size: 11px; }
    .funnel-label { font-size: 13px; }
    .funnel-weighted { font-size: 13px; text-align: right; color: #475569; }
    .stage-section { margin-bottom: 16px; page-break-inside: avoid; }
    .stage-header { background: #f1f5f9; padding: 8px 12px; display: flex; justify-content: space-between; font-weight: 600; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
    .leaderboard { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
    .leader-card { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    .leader-title { background: #f1f5f9; padding: 8px 12px; font-weight: 600; font-size: 12px; border-bottom: 1px solid #e2e8f0; }
    .leader-item { display: flex; justify-content: space-between; padding: 8px 12px; border-bottom: 1px dashed #f1f5f9; font-size: 12px; }
    .leader-item:last-child { border-bottom: none; }
    @media print { body { padding: 20px; } .page-break { page-break-before: always; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <h1>Weekly Sales Report</h1>
      <p class="date">${today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
    </div>
    <div style="text-align: right;">
      <div style="font-weight: 600; color: #1e40af;">GHR Healthcare</div>
      <div class="date">Sales Pipeline Summary</div>
    </div>
  </div>

  <div class="kpi-grid">
    <div class="kpi">
      <div class="kpi-label">Total Pipeline</div>
      <div class="kpi-value">${formatMoneyCompact(totalPipelineValue)}</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">Closed Revenue</div>
      <div class="kpi-value green">${formatMoneyCompact(closedWonDeals.reduce((s, d) => s + d.value, 0))}</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">Active Deals</div>
      <div class="kpi-value blue">${activeDeals.length}</div>
    </div>
    <div class="kpi">
      <div class="kpi-label">Late Stage (70%+)</div>
      <div class="kpi-value purple">${formatMoneyCompact(lateStageValue)}</div>
    </div>
  </div>

  <div class="two-col">
    <div>
      <h2>Service Line Totals</h2>
      <table>
        <thead><tr><th>Service Line</th><th class="text-center">Deals</th><th class="text-right">Revenue</th></tr></thead>
        <tbody>
          ${serviceLineStats.map(sl => `
            <tr>
              <td class="font-bold">${sl.name}</td>
              <td class="text-center">${sl.count}</td>
              <td class="text-right font-bold text-green">${formatMoneyCompact(sl.revenue)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
    <div>
      <h2>Recent Performance</h2>
      <div class="wins-summary">
        <div><div class="wins-label">This Week</div><div class="wins-value">${formatMoneyCompact(wonThisWeekValue)}</div></div>
        <div><div class="wins-label">Last Week</div><div class="wins-value">${formatMoneyCompact(wonLastWeekValue)}</div></div>
        <div><div class="wins-label">Quarter</div><div class="wins-value">${formatMoneyCompact(wonThisQuarterValue)}</div></div>
      </div>
      ${recentWins.length > 0 ? `
        <table>
          <thead><tr><th>Recent Wins</th><th class="text-right">Value</th></tr></thead>
          <tbody>
            ${recentWins.map(d => `<tr><td>${d.title}</td><td class="text-right font-bold text-green">${formatMoneyCompact(d.value)}</td></tr>`).join('')}
          </tbody>
        </table>
      ` : '<p style="color: #94a3b8; font-style: italic; text-align: center; padding: 16px;">No recent wins</p>'}
    </div>
  </div>

  <h2>Pipeline Funnel</h2>
  <div style="margin-bottom: 24px;">
    ${funnelData.map(stage => `
      <div class="funnel-row">
        <div class="funnel-label">${stage.label} (${(stage.probability * 100)}%)</div>
        <div class="funnel-bar">
          <div class="funnel-fill" style="width: ${Math.max((stage.value / totalPipelineValue) * 100, stage.count > 0 ? 10 : 0)}%; background-color: ${stage.color};">
            ${stage.count > 0 ? `${stage.count} - ${formatMoneyCompact(stage.value)}` : ''}
          </div>
        </div>
        <div class="funnel-weighted">${formatMoneyCompact(stage.weighted)}</div>
      </div>
    `).join('')}
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 16px; padding-top: 16px; border-top: 1px solid #e2e8f0;">
      <div><strong>Total Pipeline:</strong> ${formatMoneyCompact(totalPipelineValue)}</div>
      <div style="text-align: right;"><strong>Total Weighted:</strong> ${formatMoneyCompact(totalWeightedValue)}</div>
    </div>
  </div>

  <h2>Forecast Detail</h2>
  ${['Decision', 'Negotiation', 'Pitch', 'Discovery', 'Qualified'].map(stage => {
    const stageDeals = groupedDeals[stage];
    if (!stageDeals || stageDeals.length === 0) return '';
    const stageProb = funnelConfig.find(c => c.id === stage)?.probability || 0;
    return `
      <div class="stage-section">
        <div class="stage-header">
          <span>${stage} (${(stageProb * 100)}%)</span>
          <span style="font-weight: normal; color: #64748b;">${stageDeals.length} Deals</span>
        </div>
        <table>
          <thead><tr><th>Client / Deal</th><th>Owner</th><th class="text-center">Exp. Close</th><th class="text-right">Value</th></tr></thead>
          <tbody>
            ${stageDeals.map(deal => {
              const isPastDue = deal.expectedClose && parseLocalDate(deal.expectedClose) < parseLocalDate(new Date().toISOString().split('T')[0]);
              return `
              <tr>
                <td class="font-bold">${deal.title}</td>
                <td>${deal.owner}</td>
                <td class="text-center" ${isPastDue ? 'style="color: #dc2626; font-weight: bold;"' : ''}>${formatDateShort(deal.expectedClose)}</td>
                <td class="text-right font-bold">${formatMoney(deal.value)}</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>
      </div>
    `;
  }).join('')}

  <h2>Leaderboard</h2>
  <div class="leaderboard">
    <div class="leader-card">
      <div class="leader-title">Top Revenue</div>
      ${top3Revenue.map((p, i) => `
        <div class="leader-item">
          <span><strong>${i + 1}.</strong> ${p.name}</span>
          <span class="font-bold text-green">${formatMoneyCompact(p.revenue)}</span>
        </div>
      `).join('')}
    </div>
    <div class="leader-card">
      <div class="leader-title">Top Volume</div>
      ${top3Volume.map((p, i) => `
        <div class="leader-item">
          <span><strong>${i + 1}.</strong> ${p.name}</span>
          <span class="font-bold text-blue">${p.count} deals</span>
        </div>
      `).join('')}
    </div>
  </div>

  <div style="margin-top: 40px; padding-top: 16px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 11px;">
    Generated ${new Date().toLocaleString()} | GHR Healthcare Sales Manager
  </div>
</body>
</html>
    `;

    exportWindow.document.write(html);
    exportWindow.document.close();

    // Auto-trigger print dialog after content loads
    exportWindow.onload = () => {
      exportWindow.print();
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center border-b pb-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Weekly Sales Report</h1>
          <p className="text-xs text-slate-500">{today.toLocaleDateString()}</p>
        </div>
        <button onClick={handleExport} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded shadow-sm hover:bg-blue-700 text-sm font-medium">
          <DownloadIcon />
          Export
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded border border-slate-200">
          <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Total Pipeline</div>
          <div className="text-2xl font-bold text-slate-800">{formatMoneyCompact(totalPipelineValue)}</div>
        </div>
        <div className="bg-white p-4 rounded border border-slate-200">
          <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Closed Revenue</div>
          <div className="text-2xl font-bold text-emerald-600">{formatMoneyCompact(closedWonDeals.reduce((s, d) => s + d.value, 0))}</div>
        </div>
        <div className="bg-white p-4 rounded border border-slate-200">
          <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Active Deals</div>
          <div className="text-2xl font-bold text-blue-600">{activeDeals.length}</div>
        </div>
        <div className="bg-white p-4 rounded border border-slate-200">
          <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Late Stage (70%+)</div>
          <div className="text-2xl font-bold text-purple-600">{formatMoneyCompact(lateStageValue)}</div>
        </div>
      </div>

      {/* Service Lines & Wins Row */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Service Lines */}
        <div className="bg-white border rounded overflow-hidden h-full">
          <div className="bg-slate-50 p-2 border-b text-xs font-bold text-slate-700">Service Line Totals</div>
          <table className="w-full text-xs">
            <thead className="bg-white text-slate-400 border-b">
              <tr><th className="p-2 text-left">Line</th><th className="p-2 text-center">Deals</th><th className="p-2 text-right">Value</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {serviceLineStats.map((sl, i) => (
                <tr key={i}>
                  <td className="p-2 font-medium text-slate-700">{sl.name}</td>
                  <td className="p-2 text-center text-slate-500">{sl.count}</td>
                  <td className="p-2 text-right font-mono font-bold text-emerald-600">{formatMoneyCompact(sl.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Wins Section */}
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center bg-white border rounded p-2">
            <div className="border-r">
              <div className="text-[10px] uppercase text-green-600 font-bold">This Week</div>
              <div className="text-lg font-bold">{formatMoneyCompact(wonThisWeekValue)}</div>
            </div>
            <div className="border-r">
              <div className="text-[10px] uppercase text-green-600 font-bold">Last Week</div>
              <div className="text-lg font-bold">{formatMoneyCompact(wonLastWeekValue)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-green-600 font-bold">Quarter</div>
              <div className="text-lg font-bold">{formatMoneyCompact(wonThisQuarterValue)}</div>
            </div>
          </div>

          <div className="border rounded overflow-hidden">
            <div className="bg-slate-50 p-2 border-b text-xs font-bold text-slate-700">Recent Wins</div>
            {recentWins.length > 0 ? (
              <table className="w-full text-[10px]">
                <tbody className="divide-y divide-slate-50">
                  {recentWins.map(d => (
                    <tr key={d.id}>
                      <td className="p-1.5 pl-2">{d.title}</td>
                      <td className="p-1.5 text-right font-bold text-emerald-600 pr-2">{formatMoneyCompact(d.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="p-2 text-xs text-slate-400 italic text-center">No recent wins</div>}
          </div>
        </div>
      </div>

      {/* Pipeline Funnel */}
      <div className="border rounded-lg p-4 mb-6 bg-white">
        <h3 className="font-bold text-lg text-slate-800 mb-4">Pipeline Funnel</h3>
        <div className="space-y-2">
          {funnelData.map(stage => (
            <div key={stage.id} className="flex items-center gap-4">
              <div className="w-28 text-sm font-medium text-gray-700">{stage.label} ({(stage.probability * 100)}%)</div>
              <div className="flex-1 h-8 bg-gray-100 rounded overflow-hidden">
                <div
                  className="h-full flex items-center justify-center text-white text-xs font-bold"
                  style={{
                    width: `${Math.max((stage.value / totalPipelineValue) * 100, stage.count > 0 ? 10 : 0)}%`,
                    backgroundColor: stage.color
                  }}
                >
                  {stage.count > 0 && `${stage.count} - ${formatMoneyCompact(stage.value)}`}
                </div>
              </div>
              <div className="w-24 text-right text-sm font-medium text-gray-600">{formatMoneyCompact(stage.weighted)}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t flex justify-between text-sm">
          <span className="font-bold text-gray-700">Total Pipeline: {formatMoneyCompact(totalPipelineValue)}</span>
          <span className="font-bold text-gray-700">Total Weighted: {formatMoneyCompact(totalWeightedValue)}</span>
        </div>
      </div>

      {/* Forecast Detail */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-bold text-lg text-slate-800 mb-4">Forecast Detail</h3>
        {['Decision', 'Negotiation', 'Pitch', 'Discovery', 'Qualified'].map(stage => {
          const stageDeals = groupedDeals[stage];
          if (!stageDeals || stageDeals.length === 0) return null;
          const stageProb = funnelConfig.find(c => c.id === stage)?.probability || 0;

          return (
            <div key={stage} className="mb-6">
              <div className="bg-slate-100 p-2 border-y border-slate-200 font-bold text-sm text-slate-800 flex justify-between">
                <span>{stage} <span className="text-slate-500 font-normal">({(stageProb * 100)}%)</span></span>
                <span className="text-xs text-slate-500 font-normal">{stageDeals.length} Deals</span>
              </div>
              <table className="w-full text-[10px] text-left">
                <thead className="bg-white text-slate-400 font-semibold border-b">
                  <tr>
                    <th className="p-1 pl-2 w-1/3">Client / Deal</th>
                    <th className="p-1 w-1/6">Owner</th>
                    <th className="p-1 w-1/6 text-center">Exp. Close</th>
                    <th className="p-1 pr-2 text-right w-1/6">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stageDeals.map(deal => (
                    <tr key={deal.id}>
                      <td className="p-1 pl-2 font-medium text-slate-900">{deal.title}</td>
                      <td className="p-1 text-slate-600">{deal.owner}</td>
                      <td className={`p-1 text-center ${deal.expectedClose && parseLocalDate(deal.expectedClose) < parseLocalDate(new Date().toISOString().split('T')[0]) ? 'text-red-600 font-medium' : 'text-slate-600'}`}>{formatDateShort(deal.expectedClose)}</td>
                      <td className="p-1 pr-2 text-right font-mono font-bold text-slate-800">{formatMoney(deal.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {/* Leaderboard Summary */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-bold text-lg text-slate-800 mb-4">Leaderboard</h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="border rounded overflow-hidden">
            <div className="bg-slate-50 p-2 border-b text-xs font-bold text-slate-700">Top Revenue</div>
            <div className="p-2 space-y-2">
              {top3Revenue.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-xs border-b border-dashed border-gray-100 pb-1 last:border-0">
                  <div className="flex gap-2"><span className="font-bold text-slate-400">{i + 1}.</span><span className="font-medium text-slate-800">{p.name}</span></div>
                  <span className="font-bold text-emerald-600">{formatMoneyCompact(p.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="border rounded overflow-hidden">
            <div className="bg-slate-50 p-2 border-b text-xs font-bold text-slate-700">Top Volume</div>
            <div className="p-2 space-y-2">
              {top3Volume.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-xs border-b border-dashed border-gray-100 pb-1 last:border-0">
                  <div className="flex gap-2"><span className="font-bold text-slate-400">{i + 1}.</span><span className="font-medium text-slate-800">{p.name}</span></div>
                  <span className="font-bold text-blue-600">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Editable Field Component
function EditableField({ label, value, fieldName, type = 'text', options = null, deal, onSave, onRevert, isOverridden }) {
  const [isEditing, setIsEditing] = useState(false);
  // Convert MM/DD/YYYY to YYYY-MM-DD for date inputs (HTML date input requires YYYY-MM-DD)
  const normalizeDate = (val) => {
    if (type !== 'date' || !val) return val;
    // Check if it's in MM/DD/YYYY format
    if (val.includes('/')) {
      const [month, day, year] = val.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return val; // Already in YYYY-MM-DD format
  };
  const [editValue, setEditValue] = useState(normalizeDate(value));
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      // Convert values for Bullhorn compatibility
      let saveValue = editValue;
      if (type === 'date' && editValue) {
        // Convert date from YYYY-MM-DD to MM/DD/YYYY
        const [year, month, day] = editValue.split('-');
        saveValue = `${month}/${day}/${year}`;
      } else if (type === 'percent' && editValue !== null && editValue !== undefined) {
        // Convert percentage to decimal (50 -> 0.50)
        saveValue = (parseFloat(editValue) / 100).toFixed(2);
      } else if (type === 'money' && editValue !== null && editValue !== undefined) {
        // Format money with two decimal places (350000 -> 350000.00)
        saveValue = parseFloat(editValue).toFixed(2);
      }
      await onSave(deal.id, fieldName, saveValue);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevert = async () => {
    if (!isOverridden) return;
    setIsSaving(true);
    try {
      const response = await fetch(`/api/deals/${deal.id}/overrides/${fieldName}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        if (onRevert) onRevert();
      }
    } catch (err) {
      console.error('Failed to revert:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const displayValue = type === 'money' ? formatMoney(value) :
                       type === 'date' ? formatDateShort(value) :
                       type === 'percent' ? `${value}%` : value;

  return (
    <div className="group relative">
      <div className="text-xs font-bold text-gray-500 uppercase mb-1">
        {label}
      </div>
      {isEditing ? (
        <div className="flex gap-2 items-center">
          {options ? (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : type === 'money' ? (
            <input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(parseFloat(e.target.value) || 0)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : type === 'percent' ? (
            <input
              type="number"
              min="0"
              max="100"
              value={editValue}
              onChange={(e) => setEditValue(parseInt(e.target.value) || 0)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : type === 'date' ? (
            <input
              type="date"
              value={editValue || ''}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          ) : type === 'html' ? (
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
              autoFocus
            />
          ) : (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="p-1 text-green-600 hover:bg-green-50 rounded"
          >
            <CheckIcon />
          </button>
          <button
            onClick={() => { setIsEditing(false); setEditValue(value); }}
            className="p-1 text-gray-400 hover:bg-gray-100 rounded"
          >
            <XIcon />
          </button>
        </div>
      ) : (
        <div className="flex items-start gap-2 bg-white p-2 rounded border shadow-sm">
          {type === 'html' ? (
            <div
              className="flex-1 font-medium prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: value || '-' }}
            />
          ) : (
            <span className="flex-1 font-medium">{displayValue || '-'}</span>
          )}
          <button
            onClick={() => setIsEditing(true)}
            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-opacity flex-shrink-0"
            title="Edit"
          >
            <EditIcon />
          </button>
          {isOverridden && (
            <button
              onClick={handleRevert}
              disabled={isSaving}
              className="opacity-0 group-hover:opacity-100 p-1 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded transition-opacity flex-shrink-0"
              title="Revert to original"
            >
              <UndoIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Service lines for cross-selling
const SERVICE_LINES = ['Business Development', 'Search', 'Locum Tenens', 'Allied', 'Rev Cycle', 'IT'];

// Deal Row Component
function DealRow({ deal, isExpanded, onToggle, onUpdateDeal, canSave = true }) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [savingCrossSell, setSavingCrossSell] = useState(false);

  const overrides = deal._overrides || {};

  // Local state for cross-sell with optimistic updates
  const [localCrossSell, setLocalCrossSell] = useState(deal.crossSellPotential || []);

  // Sync local state when deal prop updates (e.g., after refresh)
  useEffect(() => {
    setLocalCrossSell(deal.crossSellPotential || []);
  }, [deal.crossSellPotential]);

  // Use local state for display (allows optimistic updates)
  const crossSellSelections = localCrossSell;

  // Get service lines available for cross-sell (exclude current division)
  const availableCrossSellLines = SERVICE_LINES.filter(sl => sl !== deal.division);

  const handleCrossSellToggle = async (serviceLine) => {
    if (!canSave) {
      alert('Cannot save changes - database connection is unavailable. Please wait for reconnection.');
      return;
    }

    const newSelections = crossSellSelections.includes(serviceLine)
      ? crossSellSelections.filter(s => s !== serviceLine)
      : [...crossSellSelections, serviceLine];

    // Optimistic update - update local state immediately
    setLocalCrossSell(newSelections);

    setSavingCrossSell(true);
    try {
      const response = await fetch(`/api/deals/${deal.id}/overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fieldName: 'crossSellPotential', value: newSelections })
      });
      if (response.ok) {
        if (onUpdateDeal) onUpdateDeal();
      } else {
        // Revert on failure
        setLocalCrossSell(deal.crossSellPotential || []);
      }
    } catch (err) {
      console.error('Failed to save cross-sell selection:', err);
      // Revert on error
      setLocalCrossSell(deal.crossSellPotential || []);
    } finally {
      setSavingCrossSell(false);
    }
  };

  const handleSaveOverride = async (dealId, fieldName, value) => {
    if (!canSave) {
      throw new Error('Cannot save changes - database connection is unavailable');
    }

    const response = await fetch(`/api/deals/${dealId}/overrides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fieldName, value })
    });
    if (response.ok) {
      // Refresh deals to get updated data
      if (onUpdateDeal) onUpdateDeal();
    } else {
      throw new Error('Failed to save override');
    }
  };

  const loadHistory = async () => {
    if (history.length > 0) {
      setShowHistory(!showHistory);
      return;
    }
    setLoadingHistory(true);
    try {
      const response = await fetch(`/api/deals/${deal.id}/history`);
      if (response.ok) {
        const data = await response.json();
        setHistory(data.data || []);
        setShowHistory(true);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const statusOptions = ['Identified', 'Qualified', 'Discovery', 'Pitch', 'Negotiation', 'Decision', 'Closed-Won', 'Closed-Lost'];

  return (
    <>
      <tr
        className={`hover:bg-blue-50 cursor-pointer ${isExpanded ? 'bg-blue-50' : ''}`}
        onClick={onToggle}
      >
        <td className="px-4 text-center text-gray-400">
          {isExpanded ? <ChevronDown /> : <ChevronRight />}
        </td>
        <td className="px-6 py-4 font-medium text-gray-900 min-w-[280px]">
          {deal.title.split('-')[0].trim()}
        </td>
        <td className="px-4 py-4 text-center">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${
            deal.contractType === 'MSP'
              ? 'bg-purple-50 text-purple-700 border-purple-200'
              : 'bg-slate-50 text-slate-600 border-slate-200'
          }`}>
            {deal.contractType}
          </span>
        </td>
        <td className="px-6 py-4 text-gray-600">{deal.owner}</td>
        <td className="px-6 py-4 text-gray-600 text-center text-xs">{deal.division}</td>
        <td className="px-6 py-4 text-center">
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(deal.status)}`}>
            {deal.status}
          </span>
        </td>
        <td className="px-6 py-4 text-center font-semibold text-gray-700">{deal.probability}%</td>
        <td className={`px-6 py-4 text-center text-xs font-bold ${
          deal.expectedClose && parseLocalDate(deal.expectedClose) < parseLocalDate(new Date().toISOString().split('T')[0])
            ? 'text-red-600'
            : 'text-gray-900'
        }`}>
          {formatDateShort(deal.expectedClose)}
        </td>
        <td className="px-6 py-4 text-right font-sans font-bold text-gray-800">
          {formatMoney(deal.value)}
        </td>
      </tr>
      {isExpanded && (
        <tr className="bg-slate-50 border-b border-gray-200">
          <td colSpan={9} className="p-0">
            <div className="bg-slate-100 px-12 py-3 border-b border-slate-200 flex justify-between items-center">
              <div className="flex gap-4 items-baseline">
                <span className="text-xl font-bold text-slate-900">{deal.title}</span>
                <span className="text-sm font-bold text-white uppercase bg-blue-600 px-3 py-1 rounded shadow-sm">
                  {deal.contractType}
                </span>
                <span className="text-xs text-gray-500 font-mono bg-white px-2 py-1 rounded border">
                  ID: {deal.id}
                </span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); loadHistory(); }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-blue-600 hover:bg-white rounded border border-gray-300 transition-colors"
              >
                <HistoryIcon />
                {loadingHistory ? 'Loading...' : 'History'}
              </button>
            </div>

            {/* History Panel */}
            {showHistory && (
              <div className="bg-amber-50 border-b border-amber-200 px-12 py-4">
                <div className="text-xs font-bold text-amber-800 uppercase mb-2">Change History</div>
                {history.length === 0 ? (
                  <div className="text-sm text-amber-600 italic">No changes recorded</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {history.map((entry, i) => (
                      <div key={i} className="flex items-center gap-4 text-xs bg-white p-2 rounded border border-amber-200">
                        <span className="font-mono text-gray-400">{new Date(entry.changedAt).toLocaleString()}</span>
                        <span className="font-medium text-gray-700">{entry.fieldName}</span>
                        <span className="text-gray-400">→</span>
                        <span className="text-blue-600">{entry.newValue || '(deleted)'}</span>
                        <span className="text-gray-400 ml-auto">by {entry.changedBy}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="p-6 px-12 flex gap-8 text-sm">
              {/* Left Column - Editable Fields */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <EditableField
                    label="Status"
                    value={deal.status}
                    fieldName="status"
                    options={statusOptions}
                    deal={deal}
                    onSave={handleSaveOverride}
                    onRevert={onUpdateDeal}
                    isOverridden={!!overrides.status}
                  />
                  <EditableField
                    label="Probability"
                    value={deal.probability}
                    fieldName="winProbabilityPercent"
                    type="percent"
                    deal={deal}
                    onSave={handleSaveOverride}
                    onRevert={onUpdateDeal}
                    isOverridden={!!overrides.winProbabilityPercent}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <EditableField
                    label="Value"
                    value={deal.value}
                    fieldName="dealValue"
                    type="money"
                    deal={deal}
                    onSave={handleSaveOverride}
                    onRevert={onUpdateDeal}
                    isOverridden={!!overrides.dealValue}
                  />
                  <EditableField
                    label="Expected Close"
                    value={deal.expectedClose}
                    fieldName="expectedCloseDate"
                    type="date"
                    deal={deal}
                    onSave={handleSaveOverride}
                    onRevert={onUpdateDeal}
                    isOverridden={!!overrides.expectedCloseDate}
                  />
                </div>

                {/* Cross-Selling Potential */}
                <div className="bg-blue-50 p-3 rounded border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold text-blue-800 uppercase">Cross-Selling Potential</div>
                    {savingCrossSell && (
                      <span className="text-xs text-blue-500">Saving...</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {availableCrossSellLines.map(serviceLine => (
                      <label
                        key={serviceLine}
                        className={`flex items-center gap-1.5 cursor-pointer hover:bg-blue-100 px-2 py-1 rounded transition-colors ${savingCrossSell ? 'opacity-50' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={crossSellSelections.includes(serviceLine)}
                          onChange={() => handleCrossSellToggle(serviceLine)}
                          disabled={savingCrossSell}
                          className="rounded border-blue-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                        />
                        <span className="text-xs font-medium text-blue-900">{serviceLine}</span>
                      </label>
                    ))}
                  </div>
                  {crossSellSelections.length > 0 && (
                    <div className="mt-2 text-xs text-blue-600">
                      Selected: {crossSellSelections.join(', ')}
                    </div>
                  )}
                </div>

                <EditableField
                  label="Notes"
                  value={deal.notes}
                  fieldName="notes"
                  type="html"
                  deal={deal}
                  onSave={handleSaveOverride}
                  onRevert={onUpdateDeal}
                  isOverridden={!!overrides.notes}
                />
              </div>

              {/* Right Column - Read-only Info */}
              <div className="w-72 space-y-4 border-l pl-6">
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Meeting Activity</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white p-2 rounded border">
                      <div className="text-gray-400 mb-1">Last</div>
                      <div className="font-medium">{formatDateShort(deal.lastAppt)}</div>
                    </div>
                    <div className="bg-white p-2 rounded border">
                      <div className="text-gray-400 mb-1">Next</div>
                      <div className="font-medium text-blue-600">{formatDateShort(deal.nextAppt)}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Deal Metrics</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white p-2 rounded border border-gray-200 text-center">
                      <div className="text-xs text-gray-400">Total TOA</div>
                      <div className="font-bold text-blue-600">{deal.toa}</div>
                    </div>
                    <div className="bg-white p-2 rounded border border-gray-200 text-center">
                      <div className="text-xs text-gray-400">PTOA</div>
                      <div className="font-bold text-emerald-600">{deal.ptoa}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold text-gray-500 uppercase mb-2">Contact</div>
                  <div className="bg-white p-2 rounded border text-sm space-y-1">
                    <div className="font-medium">{deal.contact || '-'}</div>
                    {deal.contactTitle && (
                      <div className="text-gray-500 text-xs">{deal.contactTitle}</div>
                    )}
                    {deal.contactEmail && (
                      <div className="text-blue-600">
                        <a href={`mailto:${deal.contactEmail}`} className="hover:underline">{deal.contactEmail}</a>
                      </div>
                    )}
                    {deal.contactPhone && (
                      <div className="text-gray-600">
                        <a href={`tel:${deal.contactPhone}`} className="hover:underline">{deal.contactPhone}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// User icon for auth
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

// Logout icon
const LogoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/>
  </svg>
);

// Filter icons
const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const HistoryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>
  </svg>
);

const UndoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/>
  </svg>
);

// Sort icons
const ArrowUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m18 15-6-6-6 6"/>
  </svg>
);

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6"/>
  </svg>
);

// Calendar icon for date presets
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/>
  </svg>
);

// Mail icon for email requests
const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

// Sort indicator component
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <span className="ml-1 opacity-30"><ArrowDownIcon /></span>;
  }
  return (
    <span className="ml-1 text-blue-600">
      {sortConfig.direction === 'ascending' ? <ArrowUpIcon /> : <ArrowDownIcon />}
    </span>
  );
};

// Date preset helper functions
// Get {start, end} Date range for a quarter string like "2025-Q1"
const getQuarterRangeFromString = (quarterStr) => {
  const [yearStr, qStr] = quarterStr.split('-Q');
  const year = parseInt(yearStr, 10);
  const q = parseInt(qStr, 10);
  const start = new Date(year, (q - 1) * 3, 1);
  const end = new Date(year, q * 3, 0, 23, 59, 59, 999);
  return { start, end };
};

// Build list of available quarters from the oldest deal date through the current quarter, newest first
const getAvailableQuarters = (deals = []) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentQuarter = Math.floor(today.getMonth() / 3) + 1;

  // Find the oldest date across all deals (actualClose or expectedClose)
  let oldest = null;
  for (const d of deals) {
    const dates = [d.actualClose, d.expectedClose].map(parseLocalDate).filter(Boolean);
    for (const dt of dates) {
      if (!oldest || dt < oldest) oldest = dt;
    }
  }

  const startYear = oldest ? oldest.getFullYear() : currentYear;
  const startQuarter = oldest ? Math.floor(oldest.getMonth() / 3) + 1 : currentQuarter;

  const quarters = [];
  for (let y = startYear; y <= currentYear; y++) {
    const minQ = y === startYear ? startQuarter : 1;
    const maxQ = y === currentYear ? currentQuarter : 4;
    for (let q = minQ; q <= maxQ; q++) {
      quarters.push(`${y}-Q${q}`);
    }
  }
  return quarters.reverse();
};

const getDatePresetRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const getStartOfWeek = (d) => {
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  };

  let start = null;
  let end = null;

  switch (preset) {
    case 'thisWeek':
      start = getStartOfWeek(new Date(today));
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      break;
    case 'lastWeek':
      start = getStartOfWeek(new Date(today));
      start.setDate(start.getDate() - 7);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      break;
    case 'thisMonth':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'lastMonth':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'thisQuarter': {
      const q = Math.floor((today.getMonth() + 3) / 3);
      start = new Date(today.getFullYear(), (q - 1) * 3, 1);
      end = new Date(today.getFullYear(), q * 3, 0);
      break;
    }
    case 'lastQuarter': {
      const q = Math.floor((today.getMonth() + 3) / 3);
      start = new Date(today.getFullYear(), (q - 2) * 3, 1);
      end = new Date(today.getFullYear(), (q - 1) * 3, 0);
      break;
    }
    case 'thisYear':
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
      break;
    case 'nextWeek':
      start = getStartOfWeek(new Date(today));
      start.setDate(start.getDate() + 7);
      end = new Date(start);
      end.setDate(end.getDate() + 6);
      break;
    case 'next2Weeks':
      start = getStartOfWeek(new Date(today));
      start.setDate(start.getDate() + 7);
      end = new Date(start);
      end.setDate(end.getDate() + 13);
      break;
    case 'nextMonth':
      start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      break;
    case 'nextQuarter': {
      const q = Math.floor((today.getMonth() + 3) / 3);
      start = new Date(today.getFullYear(), q * 3, 1);
      end = new Date(today.getFullYear(), (q + 1) * 3, 0);
      break;
    }
    default:
      return { start: null, end: null };
  }

  // Format as YYYY-MM-DD for input fields
  const formatDate = (d) => d.toISOString().split('T')[0];
  return {
    start: start ? formatDate(start) : '',
    end: end ? formatDate(end) : ''
  };
};

function App() {
  const [activeView, setActiveView] = useState('dashboard');
  const [dashboardMode, setDashboardMode] = useState('visual');
  const [expandedDealId, setExpandedDealId] = useState(null);
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null); // 'warehouse', 'test', or 'app'
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [initialHealthChecked, setInitialHealthChecked] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Connection health state
  const [connectionHealth, setConnectionHealth] = useState({
    status: 'unknown', // 'healthy', 'degraded', 'unhealthy', 'unknown'
    lastChecked: null,
    lastSuccessfulFetch: null,
    warehouseConnected: false,
    appDbConnected: false,
    isRecovering: false,
    errors: []
  });

  // User role state (4-tier: admin > department_head > team_manager > user)
  const [isAdmin, setIsAdmin] = useState(false);
  const [isManager, setIsManager] = useState(false); // true for dept_head or team_manager or admin
  const [userRole, setUserRole] = useState('user'); // raw role string
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // App config from API (version, etc.)
  const [appConfig, setAppConfig] = useState({ appVersion: '...', appName: 'GHR Sales Manager' });

  // KPI state
  const [kpiGoals, setKpiGoals] = useState({});
  const [userDepartments, setUserDepartments] = useState({}); // owner -> department overrides (persistent)
  const [kpiTimeframe, setKpiTimeframe] = useState('Weekly');
  const [activityData, setActivityData] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [warehouseDepartments, setWarehouseDepartments] = useState([]);
  const [noteMeetings, setNoteMeetings] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    division: '',
    owner: '',
    contractType: '',
    datePreset: '',
    dateFrom: '',
    dateTo: ''
  });

  // Multi-select stage filter
  const [filterStages, setFilterStages] = useState([]);
  const [isStageDropdownOpen, setIsStageDropdownOpen] = useState(false);

  // Multi-select quarter filter (e.g. ["2025-Q1", "2025-Q4"])
  const [filterQuarters, setFilterQuarters] = useState([]);
  const [isQuarterDropdownOpen, setIsQuarterDropdownOpen] = useState(false);

  // Snapshot comparison state
  const [snapshots, setSnapshots] = useState([]);
  const [comparisonSnapshot, setComparisonSnapshot] = useState(null);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isStageDropdownOpen && !e.target.closest('.stage-dropdown-container')) {
        setIsStageDropdownOpen(false);
      }
      if (isQuarterDropdownOpen && !e.target.closest('.quarter-dropdown-container')) {
        setIsQuarterDropdownOpen(false);
      }
      if (isSnapshotDropdownOpen && !e.target.closest('.snapshot-dropdown-container')) {
        setIsSnapshotDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isStageDropdownOpen, isQuarterDropdownOpen, isSnapshotDropdownOpen]);

  // Fetch app config (version, etc.) on mount
  useEffect(() => {
    fetch('/api/config')
      .then(res => res.json())
      .then(result => {
        if (result.success && result.data) {
          setAppConfig(result.data);
        }
      })
      .catch(err => console.log('Failed to fetch config:', err.message));
  }, []);

  // Fetch available snapshots
  const fetchSnapshots = async () => {
    try {
      const response = await fetch('/api/snapshots');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSnapshots(result.data || []);
        }
      }
    } catch (err) {
      console.log('Failed to fetch snapshots:', err.message);
    }
  };

  // Fetch departments from warehouse
  const fetchDepartments = async () => {
    try {
      const response = await fetch('/api/departments');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setWarehouseDepartments(result.data?.departments || []);
        }
      }
    } catch (err) {
      console.log('Failed to fetch departments:', err.message);
    }
  };

  // Load a snapshot for comparison
  const loadSnapshot = async (snapshotDate) => {
    if (!snapshotDate) {
      setComparisonSnapshot(null);
      setIsSnapshotDropdownOpen(false);
      return;
    }
    try {
      const response = await fetch(`/api/snapshots/${snapshotDate}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setComparisonSnapshot(result.data);
        }
      }
    } catch (err) {
      console.error('Failed to load snapshot:', err.message);
    }
    setIsSnapshotDropdownOpen(false);
  };

  // Save current state as snapshot
  const saveSnapshot = async () => {
    setIsSavingSnapshot(true);
    try {
      const response = await fetch('/api/snapshots', { method: 'POST' });
      const result = await response.json();
      if (response.ok && result.success) {
        alert(`Snapshot saved: ${result.data.snapshotName}`);
        fetchSnapshots(); // Refresh list
      } else {
        console.error('Snapshot save failed:', result.error || response.statusText);
        alert(`Failed to save snapshot: ${result.error || response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to save snapshot:', err.message);
      alert(`Failed to save snapshot: ${err.message}`);
    }
    setIsSavingSnapshot(false);
  };

  // Fetch snapshots and departments after auth is checked
  useEffect(() => {
    if (authChecked) {
      fetchSnapshots();
      fetchDepartments();
    }
  }, [authChecked]);

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'probability', direction: 'descending' });

  // Fetch user info from Azure SWA auth + DB role check
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const swaResponse = await fetch('/.auth/me');
        if (swaResponse.ok) {
          const data = await swaResponse.json();
          if (data.clientPrincipal) {
            setUser(data.clientPrincipal);
            setUserEmail(data.clientPrincipal.userDetails || '');
            // Check effective role from API (checks DB for manager status)
            try {
              const roleResponse = await fetch('/api/auth/me');
              if (roleResponse.ok) {
                const roleData = await roleResponse.json();
                if (roleData.success) {
                  const role = roleData.data.role;
                  setUserRole(role);
                  setIsAdmin(role === 'admin');
                  setIsManager(role === 'department_head' || role === 'team_manager' || role === 'admin');
                  setUserDisplayName(roleData.data.displayName || '');
                }
              }
            } catch (roleErr) {
              console.log('Role check not available:', roleErr.message);
            }
            setAuthChecked(true);
          } else {
            // No user logged in - redirect to Azure AD login
            window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
            return;
          }
        } else {
          window.location.href = '/.auth/login/aad?post_login_redirect_uri=' + encodeURIComponent(window.location.pathname);
          return;
        }
      } catch (err) {
        // Auth endpoint may not exist in dev - that's ok, allow app to continue
        console.log('Auth not available (dev mode):', err.message);
        setAuthChecked(true);
      }
    };
    fetchUser();
  }, []);

  // Fetch KPI activity data
  const fetchActivityData = async () => {
    setLoadingActivities(true);
    try {
      const timeframeMap = { 'Weekly': 'weekly', 'Monthly': 'monthly', 'Quarterly': 'quarterly' };
      const response = await fetch(`/api/activities?timeframe=${timeframeMap[kpiTimeframe] || 'monthly'}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setActivityData(result.data.metrics || []);
        }
      }
    } catch (err) {
      console.log('Failed to fetch activity data:', err.message);
    }
    setLoadingActivities(false);
  };

  // Fetch KPI goals
  const fetchKpiGoals = async () => {
    try {
      const response = await fetch('/api/goals?format=map');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setKpiGoals(result.data.goals || {});
        }
      }
    } catch (err) {
      console.log('Failed to fetch KPI goals:', err.message);
    }
  };

  // Fetch user department overrides (persistent, not tied to quarter)
  const fetchUserDepartments = async () => {
    try {
      const response = await fetch('/api/user-departments?format=map');
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setUserDepartments(result.data || {});
        }
      }
    } catch (err) {
      console.log('Failed to fetch user departments:', err.message);
    }
  };

  // Fetch activity data when KPI view is active or timeframe changes
  useEffect(() => {
    if (activeView === 'kpis') {
      fetchActivityData();
      fetchKpiGoals();
      fetchUserDepartments();
    }
  }, [activeView, kpiTimeframe]);

  // Fetch note-based meetings when meetings view is active
  const fetchNoteMeetings = async () => {
    try {
      const response = await fetch('/api/activities/meetings?timeframe=quarterly');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setNoteMeetings(result.data || []);
        }
      }
    } catch (err) {
      console.log('Failed to fetch note meetings:', err.message);
    }
  };

  useEffect(() => {
    if (activeView === 'meetings') {
      fetchNoteMeetings();
    }
  }, [activeView]);

  // Fetch KPI goals and departments when admin or team view is active
  useEffect(() => {
    if (activeView === 'admin' || activeView === 'team') {
      fetchKpiGoals();
      fetchActivityData();
      fetchUserDepartments();
    }
  }, [activeView]);

  // Save KPI goal (without division - that's saved separately)
  const saveKpiGoal = async (owner, goals) => {
    try {
      // Save goals (without division)
      const { division, ...goalsWithoutDivision } = goals;
      const response = await fetch('/api/goals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner, ...goalsWithoutDivision })
      });
      if (response.ok) {
        await fetchKpiGoals(); // Refresh goals
        return true;
      }
      const errorData = await response.json().catch(() => ({}));
      alert(`Failed to save goals for ${owner}: ${errorData.error || response.statusText}`);
      return false;
    } catch (err) {
      console.error('Failed to save KPI goal:', err.message);
      alert(`Failed to save goals for ${owner}: ${err.message}`);
      return false;
    }
  };

  // Save user department override (persistent, not tied to quarter)
  const saveUserDepartment = async (owner, department) => {
    try {
      const response = await fetch('/api/user-departments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner, department })
      });
      if (response.ok) {
        fetchUserDepartments(); // Refresh departments
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to save user department:', err.message);
      return false;
    }
  };

  // Fetch deals from API
  const fetchDeals = async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      const response = await fetch('/api/deals');
      if (!response.ok) {
        throw new Error(`Failed to fetch deals: ${response.statusText}`);
      }
      const result = await response.json();

      // Log debug info to console
      if (result._debug) {
        console.group('🔌 API Connection Debug');
        console.log('Data Source:', result._debug.source);
        console.log('USE_TEST_DATA:', result._debug.useTestData);
        console.log('Warehouse Configured:', result._debug.warehouseConfigured);
        console.log('Warehouse Server:', result._debug.warehouseServer || 'not set');
        console.log('Warehouse Database:', result._debug.warehouseDatabase || 'not set');
        console.log('Deal Count:', result._debug.dealCount);
        if (result._debug.warehouseError) {
          console.error('Warehouse Error:', result._debug.warehouseError);
        }
        console.groupEnd();
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch deals');
      }
      setDeals(result.data || []);
      // Track data source for warning banner
      if (result._debug?.source) {
        setDataSource(result._debug.source);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  // Initial fetch - wait for auth check to complete
  useEffect(() => {
    if (authChecked) {
      fetchDeals();
    }
  }, [authChecked]);

  // Health check function - verifies database connectivity
  const checkHealth = async () => {
    try {
      const response = await fetch('/api/health');
      if (!response.ok) {
        throw new Error('Health check failed');
      }
      const health = await response.json();

      const newHealthState = {
        status: health.status,
        lastChecked: new Date().toISOString(),
        lastSuccessfulFetch: connectionHealth.lastSuccessfulFetch,
        warehouseConnected: health.services?.warehouse === 'connected',
        appDbConnected: health.services?.overrideDb === 'connected',
        isRecovering: false,
        errors: health.errors || []
      };

      // If we were unhealthy/degraded and now healthy, refresh data
      const wasUnhealthy = connectionHealth.status === 'unhealthy' || connectionHealth.status === 'degraded';
      const nowHealthy = health.status === 'healthy';

      // Clear connection error if warehouse is now connected
      if (nowHealthy) {
        setConnectionError(null);
      }

      if (wasUnhealthy && nowHealthy) {
        console.log('🔄 Connection restored - refreshing data...');
        newHealthState.isRecovering = true;
        setConnectionHealth(newHealthState);
        await fetchDeals(false);
        setConnectionHealth(prev => ({ ...prev, isRecovering: false, lastSuccessfulFetch: new Date().toISOString() }));
      } else {
        setConnectionHealth(newHealthState);
      }

      return health;
    } catch (err) {
      console.error('Health check error:', err);
      setConnectionHealth(prev => ({
        ...prev,
        status: 'unhealthy',
        lastChecked: new Date().toISOString(),
        errors: ['Failed to reach health endpoint']
      }));
      return null;
    }
  };

  // Periodic health check / keepalive (every 30 seconds)
  // This keeps the database connection alive and detects issues early
  useEffect(() => {
    if (!authChecked) return;

    // Initial health check
    const doInitialHealthCheck = async () => {
      const result = await checkHealth();
      setInitialHealthChecked(true);
      // Only block if warehouse (main data source) is unhealthy
      // Snapshot database operations are attempted on-demand
      if (!result || result.status === 'unhealthy') {
        setConnectionError(result?.errors?.[0] || 'Failed to connect to database');
      } else {
        setConnectionError(null);
      }
    };
    doInitialHealthCheck();

    // Run health check every 30 seconds to keep connection alive
    const healthInterval = setInterval(() => {
      checkHealth();
    }, 30000); // 30 seconds

    return () => clearInterval(healthInterval);
  }, [authChecked]);

  // Visibility change detection - check health when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && authChecked) {
        const now = new Date();
        const lastFetch = connectionHealth.lastSuccessfulFetch
          ? new Date(connectionHealth.lastSuccessfulFetch)
          : null;

        // If data is stale (more than 5 minutes old), check health and refresh
        const isStale = !lastFetch || (now - lastFetch) > 5 * 60 * 1000;

        if (isStale) {
          console.log('📋 Page became visible with stale data - checking connection...');
          const health = await checkHealth();

          // If warehouse is healthy, clear any error and refresh
          if (health?.status === 'healthy' || health?.status === 'degraded') {
            console.log('✅ Connection healthy - refreshing data...');
            setConnectionError(null); // Clear any previous connection error
            await fetchDeals(false);
            setConnectionHealth(prev => ({ ...prev, lastSuccessfulFetch: new Date().toISOString() }));
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [authChecked, connectionHealth.lastSuccessfulFetch]);

  // Update lastSuccessfulFetch when deals are loaded successfully
  useEffect(() => {
    if (deals.length > 0 && dataSource === 'warehouse') {
      setConnectionHealth(prev => ({
        ...prev,
        lastSuccessfulFetch: new Date().toISOString()
      }));
    }
  }, [deals, dataSource]);

  // Helper to get effective division for a deal (considering department overrides)
  const getEffectiveDivision = (deal) => {
    return userDepartments[deal.owner] || deal.division;
  };

  const uniqueDivisions = [...new Set([
    ...deals.map(d => getEffectiveDivision(d)),
    ...activityData.map(a => userDepartments[a.owner] || a.division),
    ...Object.values(kpiGoals).map(g => userDepartments[g.owner] || g.division),
    ...Object.values(userDepartments),
  ].filter(Boolean))].sort();
  const uniqueOwners = [...new Set([
    ...deals.map(d => d.owner),
    ...activityData.map(a => a.owner),
    ...Object.keys(kpiGoals || {}),
    ...Object.keys(userDepartments || {}),
  ].filter(Boolean))].sort();
  const uniqueContractTypes = [...new Set(deals.map(d => d.contractType).filter(Boolean))].sort();

  // Get all unique statuses from deals for dynamic stage dropdown
  const uniqueStatuses = [...new Set(deals.map(d => d.status).filter(Boolean))];
  // Define the preferred order for stages
  const stageOrder = ['Identified', 'Qualified', 'Discovery', 'Pitch', 'Negotiation', 'Decision', 'Closed-Won', 'Closed-Lost'];
  const sortedStatuses = uniqueStatuses.sort((a, b) => {
    const aIdx = stageOrder.indexOf(a);
    const bIdx = stageOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  // Filter out closed-lost and calculate stats
  const allActiveDeals = deals.filter(d => d.status !== 'Closed-Lost' && d.status !== 'Closed-Won');
  const closedWonDeals = deals.filter(d => d.status === 'Closed-Won');

  // Apply filters to active deals (using effective division with overrides)
  const filteredDeals = allActiveDeals.filter(deal => {
    if (filters.division && getEffectiveDivision(deal) !== filters.division) return false;
    if (filters.owner && deal.owner !== filters.owner) return false;
    // Multi-select stage filter
    if (filterStages.length > 0 && !filterStages.includes(deal.status)) return false;
    if (filters.contractType && deal.contractType !== filters.contractType) return false;
    if (filters.dateFrom) {
      const closeDate = new Date(deal.expectedClose);
      const fromDate = new Date(filters.dateFrom);
      if (isNaN(closeDate.getTime()) || closeDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const closeDate = new Date(deal.expectedClose);
      const toDate = new Date(filters.dateTo);
      if (isNaN(closeDate.getTime()) || closeDate > toDate) return false;
    }
    // Multi-select quarter filter (deal passes if its effective close date falls in ANY selected quarter)
    if (filterQuarters.length > 0) {
      const effectiveClose = parseLocalDate(deal.actualClose) || parseLocalDate(deal.expectedClose);
      if (!effectiveClose) return false;
      const inAny = filterQuarters.some(q => {
        const { start, end } = getQuarterRangeFromString(q);
        return effectiveClose >= start && effectiveClose <= end;
      });
      if (!inAny) return false;
    }
    return true;
  });

  // Sort handler
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered deals
  const activeDeals = [...filteredDeals].sort((a, b) => {
    let aVal, bVal;

    switch (sortConfig.key) {
      case 'title':
        aVal = (a.title || '').toLowerCase();
        bVal = (b.title || '').toLowerCase();
        break;
      case 'owner':
        aVal = (a.owner || '').toLowerCase();
        bVal = (b.owner || '').toLowerCase();
        break;
      case 'division':
        aVal = (a.division || '').toLowerCase();
        bVal = (b.division || '').toLowerCase();
        break;
      case 'probability':
        aVal = a.probability || 0;
        bVal = b.probability || 0;
        break;
      case 'expectedClose':
        aVal = a.expectedClose ? new Date(a.expectedClose).getTime() : 0;
        bVal = b.expectedClose ? new Date(b.expectedClose).getTime() : 0;
        break;
      case 'value':
        aVal = a.value || 0;
        bVal = b.value || 0;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  // Check if any filters are active
  const hasActiveFilters = filters.division || filters.owner || filterStages.length > 0 || filterQuarters.length > 0 || filters.contractType || filters.dateFrom || filters.dateTo || filters.datePreset;

  // Toggle stage in multi-select filter
  const toggleStageFilter = (stageId) => {
    setFilterStages(prev =>
      prev.includes(stageId)
        ? prev.filter(s => s !== stageId)
        : [...prev, stageId]
    );
  };

  // Toggle quarter in multi-select filter
  const toggleQuarterFilter = (quarter) => {
    setFilterQuarters(prev =>
      prev.includes(quarter)
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const availableQuarters = getAvailableQuarters(deals);

  // Handle funnel stage click - filter to that stage
  const handleFunnelStageClick = (stageId) => {
    setFilterStages([stageId]);
    setDashboardMode('list');
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    if (!preset) {
      setFilters({ ...filters, datePreset: '', dateFrom: '', dateTo: '' });
      return;
    }
    const range = getDatePresetRange(preset);
    setFilters({
      ...filters,
      datePreset: preset,
      dateFrom: range.start,
      dateTo: range.end
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({ division: '', owner: '', contractType: '', datePreset: '', dateFrom: '', dateTo: '' });
    setFilterStages([]);
    setFilterQuarters([]);
  };

  // Export deals to CSV
  const handleExportCSV = () => {
    const headers = ['ID', 'Title', 'Contact', 'Owner', 'VMS', 'Contract Type', 'Department', 'Probability', 'Status', 'Value', 'Expected Close', 'TOA', 'PTOA', 'Last Appt', 'Next Appt', 'Notes', 'DHC ID', 'Actual Close'];
    const rows = activeDeals.map(d => [
      d.id,
      `"${(d.title || '').replace(/"/g, '""')}"`,
      `"${(d.contact || '').replace(/"/g, '""')}"`,
      `"${(d.owner || '').replace(/"/g, '""')}"`,
      `"${(d.vms || '').replace(/"/g, '""')}"`,
      d.contractType || '',
      d.division || '',
      d.probability || 0,
      d.status || '',
      d.value || 0,
      d.expectedClose || '',
      d.toa || 0,
      d.ptoa || 0,
      d.lastAppt || '',
      d.nextAppt || '',
      `"${(d.notes || '').replace(/"/g, '""')}"`,
      d.dhcId || '',
      d.actualClose || ''
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `deals-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Generate email for deals needing updates
  const handleRequestUpdates = () => {
    const updatesNeeded = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check each active deal for issues: $0 value OR past expected close date
    activeDeals.forEach(deal => {
      const issues = [];

      // Check for $0 value
      if (!deal.value || deal.value === 0) {
        issues.push("Deal amount is $0 - please update with expected value");
      }

      // Check for past expected close date
      if (deal.expectedClose) {
        const closeDate = new Date(deal.expectedClose);
        closeDate.setHours(0, 0, 0, 0);
        if (closeDate < today) {
          issues.push("Expected close date is in the past - please update");
        }
      }

      if (issues.length > 0) {
        updatesNeeded.push({
          division: deal.division || 'Unknown Division',
          owner: deal.owner || 'Unknown Owner',
          title: deal.title,
          status: deal.status,
          value: deal.value,
          expectedClose: deal.expectedClose,
          issues
        });
      }
    });

    if (updatesNeeded.length === 0) {
      alert('All deals look good! No updates needed.');
      return;
    }

    // Group by division, then by owner
    const grouped = {};
    updatesNeeded.forEach(item => {
      if (!grouped[item.division]) grouped[item.division] = {};
      if (!grouped[item.division][item.owner]) grouped[item.division][item.owner] = [];
      grouped[item.division][item.owner].push(item);
    });

    // Build email body
    let body = "Hi Team,\n\nPlease review and update the following pipeline opportunities:\n\n";

    Object.entries(grouped).forEach(([division, owners]) => {
      body += `=== ${division.toUpperCase()} ===\n\n`;
      Object.entries(owners).forEach(([owner, dealsList]) => {
        body += `Owner: ${owner}\n`;
        body += "-".repeat(40) + "\n";
        dealsList.forEach(deal => {
          body += `\nDeal: ${deal.title}\n`;
          body += `Status: ${deal.status} | Value: ${formatMoney(deal.value)}${deal.expectedClose ? ` | Exp Close: ${deal.expectedClose}` : ''}\n`;
          body += "Updates Needed:\n";
          deal.issues.forEach(issue => {
            body += `  • ${issue}\n`;
          });
        });
        body += "\n";
      });
      body += "\n";
    });

    body += "---\nGenerated by GHR Sales Manager\n";

    // Open email client
    const subject = `Action Required: Pipeline Updates Needed (${updatesNeeded.length} deals)`;

    // Truncate body if too long (mailto URLs have ~2000 char limit in some browsers)
    let finalBody = body;
    const maxBodyLength = 1500;
    if (body.length > maxBodyLength) {
      finalBody = body.substring(0, maxBodyLength) + "\n\n... (truncated - " + updatesNeeded.length + " total deals need updates)\n";
    }

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(finalBody)}`;

    // Create a temporary link and click it - most reliable cross-browser method
    const link = document.createElement('a');
    link.href = mailtoUrl;
    link.click();
  };

  // Pipeline = only Qualified through Decision (excludes Identified, Closed-Won, Closed-Lost)
  const pipelineStageIds = funnelConfig.filter(s => s.id !== 'Identified').map(s => s.id); // ['Qualified', 'Discovery', 'Pitch', 'Negotiation', 'Decision']
  const pipelineDeals = activeDeals.filter(d => pipelineStageIds.includes(d.status));
  const totalPipelineValue = pipelineDeals.reduce((sum, d) => sum + d.value, 0);
  const weightedPipelineValue = pipelineDeals.reduce((sum, d) => sum + (d.value * (d.probability / 100)), 0);
  const totalClosedValue = closedWonDeals.reduce((sum, d) => sum + d.value, 0);
  const totalDeals = pipelineDeals.length;

  // Date helpers for won deals calculations
  const today = new Date();
  const getWeekRange = (offset) => {
    const d = new Date(today);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    const start = new Date(d.setDate(diff));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };
  const checkDateInRange = (dateStr, range) => {
    if (!dateStr) return false;
    const d = parseLocalDate(dateStr);
    if (!d || isNaN(d.getTime())) return false;
    return d >= range.start && d <= range.end;
  };
  const thisWeek = getWeekRange(0);
  const wonThisWeek = closedWonDeals.filter(d => checkDateInRange(d.actualClose, thisWeek));
  const wonThisWeekValue = wonThisWeek.reduce((s, d) => s + d.value, 0);

  // Quarter calculations
  const getQuarterRange = () => {
    const q = Math.floor((today.getMonth() + 3) / 3);
    const start = new Date(today.getFullYear(), (q - 1) * 3, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(today.getFullYear(), q * 3, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };
  const thisQuarter = getQuarterRange();
  const wonThisQuarter = closedWonDeals.filter(d => checkDateInRange(d.actualClose, thisQuarter));
  const wonThisQuarterValue = wonThisQuarter.reduce((s, d) => s + d.value, 0);

  // Late stage deals (70%+)
  const lateStageDeals = activeDeals.filter(d => d.probability >= 70);
  const lateStageValue = lateStageDeals.reduce((sum, d) => sum + d.value, 0);

  // Calculate funnel data
  const funnelData = funnelConfig.map(stage => {
    const stageDeals = activeDeals.filter(d => d.status === stage.id);
    const count = stageDeals.length;
    const value = stageDeals.reduce((sum, d) => sum + d.value, 0);
    const weighted = value * stage.probability;
    return { ...stage, count, value, weighted };
  });

  // Total weighted excludes Identified (same as pipeline totals)
  const totalWeightedValue = funnelData.filter(s => s.id !== 'Identified').reduce((sum, s) => sum + s.weighted, 0);

  // Retry connection function
  const retryConnection = async () => {
    setConnectionError(null);
    setInitialHealthChecked(false);
    const result = await checkHealth();
    setInitialHealthChecked(true);
    // Only block if warehouse (main data source) is unhealthy
    if (!result || result.status === 'unhealthy') {
      setConnectionError(result?.errors?.[0] || 'Failed to connect to database');
    } else {
      setConnectionError(null);
      // If healthy, also refresh data
      await fetchDeals(false);
    }
  };

  // Determine if we're still initializing (auth + data + health check)
  const isInitializing = !authChecked || isLoading || !initialHealthChecked;
  const hasConnectionError = connectionError && initialHealthChecked && !isLoading;

  // Show connection validation overlay during initial load
  if (isInitializing || hasConnectionError) {
    return (
      <div className="fixed inset-0 bg-slate-900/90 z-[200] flex items-center justify-center backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl w-[450px] border border-slate-200 overflow-hidden">
          {/* Connecting State */}
          {!hasConnectionError && (
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-blue-600 animate-pulse" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                </svg>
              </div>
              <h3 className="font-bold text-slate-800 text-xl mb-2">Connecting to Database</h3>
              <p className="text-slate-500 text-sm mb-4">
                {!authChecked ? 'Authenticating...' : isLoading ? 'Loading data...' : 'Validating connection...'}
              </p>
              <div className="flex justify-center">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
            </div>
          )}

          {/* Failed State */}
          {hasConnectionError && (
            <div>
              <div className="bg-red-50 p-5 border-b border-red-200 flex items-center gap-4">
                <div className="bg-red-100 rounded-full p-3">
                  <svg className="w-8 h-8 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-red-800 text-xl">Connection Failed</h3>
                  <p className="text-red-600 text-sm">Unable to connect to database</p>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                  <p className="text-amber-800 text-sm">
                    <span className="font-semibold">Error:</span> {connectionError || error}
                  </p>
                </div>

                <p className="text-slate-600 text-sm mb-4">
                  The database may be temporarily unavailable. Please check your network connection and try again.
                </p>

                <button
                  onClick={retryConnection}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Retry Connection
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show error state if data failed to load but connection is healthy
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold mb-2">Error loading deals</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-20">
      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Connection Status Banners */}
      {/* Recovering Banner */}
      {connectionHealth.isRecovering && (
        <div className="bg-blue-500 text-white px-4 py-3 text-center font-bold sticky top-0 z-[60] shadow-lg no-print">
          <div className="flex items-center justify-center gap-3">
            <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            <span>Reconnecting to database...</span>
          </div>
        </div>
      )}

      {/* Connection Lost Banner (unhealthy and not test mode) */}
      {!connectionHealth.isRecovering && connectionHealth.status === 'unhealthy' && dataSource !== 'test' && (
        <div className="bg-red-600 text-white px-4 py-3 text-center font-bold sticky top-0 z-[60] shadow-lg no-print">
          <div className="flex items-center justify-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>
            </svg>
            <span>DATABASE CONNECTION LOST - Changes may not be saved. Data may be stale.</span>
            <button
              onClick={() => checkHealth()}
              className="ml-2 px-3 py-1 bg-white text-red-600 rounded text-sm font-semibold hover:bg-red-50 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Test Data Warning Banner */}
      {!connectionHealth.isRecovering && dataSource === 'test' && connectionHealth.status !== 'unhealthy' && (
        <div className="bg-amber-500 text-white px-4 py-3 text-center font-bold sticky top-0 z-[60] shadow-lg no-print">
          <div className="flex items-center justify-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>
            </svg>
            <span>TEST DATA MODE - You are viewing sample data, not live warehouse data</span>
            <button
              onClick={() => checkHealth()}
              className="ml-2 px-3 py-1 bg-white text-amber-600 rounded text-sm font-semibold hover:bg-amber-50 transition-colors"
            >
              Check Connection
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 no-print">
        <div className="max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">G</div>
                GHR Sales
                <span className="text-xs font-normal text-gray-400 ml-1">v{appConfig.appVersion}</span>
              </h1>
              <nav className="flex bg-slate-100 p-1 rounded-lg">
                {[
                  'dashboard', 'kpis', 'meetings', 'leaderboard',
                  ...(isManager ? ['team'] : []),
                  ...(isAdmin ? ['admin'] : []),
                ].map(view => (
                  <button
                    key={view}
                    onClick={() => setActiveView(view)}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      activeView === view
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {view === 'kpis' ? 'KPIs' : view.charAt(0).toUpperCase() + view.slice(1)}
                  </button>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-3">
              {activeView === 'dashboard' && (
                <div className="flex bg-slate-100 p-1 rounded-lg">
                  <button
                    onClick={() => setDashboardMode('visual')}
                    className={`px-2 py-1 rounded text-xs flex gap-1 ${
                      dashboardMode === 'visual' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    <BarChart2 />
                  </button>
                  <button
                    onClick={() => setDashboardMode('list')}
                    className={`px-2 py-1 rounded text-xs flex gap-1 ${
                      dashboardMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    <ListIcon />
                  </button>
                </div>
              )}

              {/* User Info */}
              {user ? (
                <div className="flex items-center gap-2 pl-3 border-l border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <UserIcon />
                    <span className="font-medium">{user.userDetails || user.userId}</span>
                  </div>
                  <a
                    href="/.auth/logout"
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100"
                    title="Sign out"
                  >
                    <LogoutIcon />
                  </a>
                </div>
              ) : (
                <a
                  href="/.auth/login/aad"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  <UserIcon />
                  Sign in
                </a>
              )}
            </div>
          </div>

          {/* Second Row: Action Buttons + Filters */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            {/* Left: Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors bg-blue-600 border border-blue-600 text-white text-sm hover:bg-blue-700 shadow-sm"
              >
                <PrinterIcon />
                Weekly Report Snapshot
              </button>

              {/* Snapshot Controls */}
              <button
                onClick={saveSnapshot}
                disabled={isSavingSnapshot}
                className="flex items-center gap-1.5 text-xs font-medium bg-white border border-slate-300 px-3 py-1.5 rounded-md hover:bg-slate-50 text-slate-600 transition-colors disabled:opacity-50"
                title="Save current week's snapshot"
              >
                <SaveIcon />
                {isSavingSnapshot ? 'Saving...' : 'Save Snapshot'}
              </button>

              <div className="relative snapshot-dropdown-container">
                <button
                  onClick={() => setIsSnapshotDropdownOpen(!isSnapshotDropdownOpen)}
                  className={`flex items-center gap-1.5 text-xs font-medium border px-3 py-1.5 rounded-md transition-colors ${
                    comparisonSnapshot
                      ? 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                  }`}
                  title="Compare with previous snapshot"
                >
                  <HistoryIcon />
                  {comparisonSnapshot ? `vs ${comparisonSnapshot.snapshotName}` : 'Compare'}
                  <ChevronDown />
                </button>
                {isSnapshotDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px]">
                    <div
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 text-gray-500"
                      onClick={() => loadSnapshot(null)}
                    >
                      No comparison
                    </div>
                    {snapshots.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-400 italic">No snapshots saved</div>
                    ) : (
                      snapshots.map(snap => (
                        <div
                          key={snap.snapshotDate}
                          className={`px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm ${
                            comparisonSnapshot?.snapshotDate === snap.snapshotDate ? 'bg-purple-50 text-purple-700' : ''
                          }`}
                          onClick={() => loadSnapshot(snap.snapshotDate)}
                        >
                          <div className="font-medium">{snap.snapshotName}</div>
                          <div className="text-xs text-gray-400">{snap.dealCount} deals • {formatMoneyCompact(snap.totalPipelineValue)}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filters.division}
                onChange={(e) => setFilters({ ...filters, division: e.target.value })}
                className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Departments</option>
                {uniqueDivisions.map(div => (
                  <option key={div} value={div}>{div}</option>
                ))}
              </select>

              <select
                value={filters.owner}
                onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
                className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Owners</option>
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </select>

              <select
                value={filters.contractType}
                onChange={(e) => setFilters({ ...filters, contractType: e.target.value })}
                className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {uniqueContractTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              {/* Multi-Select Stage Filter */}
              <div className="relative stage-dropdown-container">
                <button
                  onClick={() => setIsStageDropdownOpen(!isStageDropdownOpen)}
                  className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none flex items-center gap-2 min-w-[120px] justify-between focus:ring-2 focus:ring-blue-500"
                >
                  <span className="truncate">
                    {filterStages.length === 0 ? 'All Stages' : `${filterStages.length} Selected`}
                  </span>
                  <ChevronDown />
                </button>
                {isStageDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[160px]">
                    <div
                      className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-sm border-b border-gray-100"
                      onClick={() => { setFilterStages([]); setIsStageDropdownOpen(false); }}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterStages.length === 0 ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                        {filterStages.length === 0 && <CheckIcon />}
                      </div>
                      <span>All Stages</span>
                    </div>
                    {sortedStatuses.map(status => {
                      const isSelected = filterStages.includes(status);
                      // Get color from funnelConfig if available, else use status-based color
                      const funnelStage = funnelConfig.find(s => s.id === status);
                      const statusColors = {
                        'Identified': '#9ca3af',
                        'Qualified': '#67e8f9',
                        'Discovery': '#22d3ee',
                        'Pitch': '#0ea5e9',
                        'Negotiation': '#1d4ed8',
                        'Decision': '#0f172a',
                        'Closed-Won': '#22c55e',
                        'Closed-Lost': '#ef4444'
                      };
                      const dotColor = funnelStage?.color || statusColors[status] || '#6b7280';
                      return (
                        <div
                          key={status}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-sm"
                          onClick={() => toggleStageFilter(status)}
                        >
                          <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {isSelected && <CheckIcon />}
                          </div>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }}></div>
                          <span>{status}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <select
                value={filters.datePreset}
                onChange={(e) => handleDatePresetChange(e.target.value)}
                className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none font-medium focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Time</option>
                <option value="lastWeek">Last Week</option>
                <option value="thisWeek">This Week</option>
                <option value="nextWeek">Next Week</option>
                <option value="next2Weeks">Next 2 Weeks</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisMonth">This Month</option>
                <option value="nextMonth">Next Month</option>
                <option value="lastQuarter">Last Quarter</option>
                <option value="thisQuarter">This Quarter</option>
                <option value="nextQuarter">Next Quarter</option>
                <option value="thisYear">This Year</option>
              </select>

              {/* Multi-Select Quarter Filter */}
              <div className="relative quarter-dropdown-container">
                <button
                  onClick={() => setIsQuarterDropdownOpen(!isQuarterDropdownOpen)}
                  className="bg-white border border-slate-300 text-slate-700 text-sm rounded-md px-2 py-1.5 outline-none flex items-center gap-2 min-w-[140px] justify-between focus:ring-2 focus:ring-blue-500"
                >
                  <span className="truncate">
                    {filterQuarters.length === 0 ? 'All Quarters' : `${filterQuarters.length} Quarter${filterQuarters.length === 1 ? '' : 's'}`}
                  </span>
                  <ChevronDown />
                </button>
                {isQuarterDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[180px] max-h-[320px] overflow-y-auto">
                    <div
                      className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-sm border-b border-gray-100"
                      onClick={() => { setFilterQuarters([]); }}
                    >
                      <div className={`w-4 h-4 border rounded flex items-center justify-center ${filterQuarters.length === 0 ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                        {filterQuarters.length === 0 && <CheckIcon />}
                      </div>
                      <span>All Quarters</span>
                    </div>
                    {availableQuarters.map(q => {
                      const isSelected = filterQuarters.includes(q);
                      return (
                        <div
                          key={q}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-sm"
                          onClick={() => toggleQuarterFilter(q)}
                        >
                          <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {isSelected && <CheckIcon />}
                          </div>
                          <span>{q.replace('-', ' ')}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1800px] mx-auto p-6">
        {activeView === 'dashboard' && (
          <>
            {/* Performance Pulse */}
            <div className="mb-8">
              <h2 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <BriefcaseIcon /> Performance Pulse
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-white to-blue-50 p-5 rounded-xl border border-blue-100 shadow-sm">
                  <div className="text-xs text-blue-600 font-bold uppercase mb-1">Total Pipeline</div>
                  <div className="text-2xl font-black text-slate-800">{formatMoney(totalPipelineValue)}</div>
                  <div className="text-xs text-slate-500 mt-1 font-medium flex justify-between">
                    <span>Weighted Value:</span>
                    <span className="text-slate-700 font-bold">{formatMoneyCompact(weightedPipelineValue)}</span>
                  </div>
                  {comparisonSnapshot && (() => {
                    const diff = totalPipelineValue - comparisonSnapshot.totalPipelineValue;
                    if (diff === 0) return null;
                    return (
                      <span className={`text-xs font-medium flex items-center gap-1 mt-1 ${diff > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {diff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        {diff > 0 ? '+' : ''}{formatMoneyCompact(diff)} vs snapshot
                      </span>
                    );
                  })()}
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-emerald-600 font-bold uppercase mb-1">Won (This Week)</div>
                  <div className="text-2xl font-bold text-slate-800">{formatMoneyCompact(wonThisWeekValue)}</div>
                  <div className="text-xs text-slate-400 mt-1">{wonThisWeek.length} deal{wonThisWeek.length !== 1 ? 's' : ''} closed</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-emerald-600 font-bold uppercase mb-1">Won (Quarter)</div>
                  <div className="text-2xl font-bold text-slate-800">{formatMoneyCompact(wonThisQuarterValue)}</div>
                  <div className="text-xs text-slate-400 mt-1">{wonThisQuarter.length} deal{wonThisQuarter.length !== 1 ? 's' : ''} closed</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="text-xs text-purple-600 font-bold uppercase mb-1">Late Stage (70%+)</div>
                  <div className="text-2xl font-bold text-slate-800">{formatMoneyCompact(lateStageValue)}</div>
                  <div className="text-xs text-slate-500 mt-1">{lateStageDeals.length} deal{lateStageDeals.length !== 1 ? 's' : ''} close to sign</div>
                </div>
              </div>
            </div>

            {/* Funnel Visual */}
            {dashboardMode === 'visual' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 min-h-[400px] mb-6">
                <div className="w-full flex flex-col items-center py-6">
                  <h3 className="text-3xl font-bold text-blue-900 mb-6">Coverage Pipeline – All Business Lines</h3>

                  <div className="w-full max-w-full relative">
                    <div className="grid grid-cols-12 gap-4 px-4 text-sm font-bold text-gray-400 uppercase tracking-widest mb-6">
                      <div className="col-span-3 text-center">Sales Stage</div>
                      <div className="col-span-6 text-center">Pipeline Revenue</div>
                      <div className="col-span-3 text-center">Weighted Revenue</div>
                    </div>

                    <div className="flex flex-col items-center space-y-0">
                      {funnelData.map((stage, idx) => (
                        <div
                          key={stage.id}
                          className="w-full grid grid-cols-12 gap-4 items-center h-20"
                          style={{ zIndex: 10 - idx }}
                        >
                          <div className="col-span-3 text-center pr-4 text-gray-700 font-medium text-xl flex justify-start pl-4 gap-2 items-center">
                            {stage.label} / <span className="font-bold">{(stage.probability * 100).toFixed(0)}%</span>
                          </div>

                          <div className="col-span-6 flex justify-center h-full">
                            <div
                              className="h-full flex items-center justify-center text-white font-bold text-2xl drop-shadow-md relative whitespace-nowrap px-6 -mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                              style={{
                                width: stage.width,
                                backgroundColor: stage.color,
                                clipPath: stage.clipPath
                              }}
                              onClick={() => handleFunnelStageClick(stage.id)}
                              title={`Click to filter by ${stage.label}`}
                            >
                              <span className="drop-shadow-lg flex items-center gap-2">
                                {stage.count} – {formatMoneyCompact(stage.value)}
                              </span>
                            </div>
                          </div>

                          <div className="col-span-3 text-center pl-4 text-gray-700 font-medium text-xl">
                            {formatMoneyCompact(stage.weighted)}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 grid grid-cols-12 gap-4 px-4 border-t border-gray-100 pt-4">
                      <div className="col-span-3"></div>
                      <div className="col-span-6 text-center">
                        <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Total Pipeline</div>
                        <div className="text-2xl font-extrabold text-emerald-600 tracking-tight">
                          {totalDeals} – {formatMoneyCompact(totalPipelineValue)}
                        </div>
                      </div>
                      <div className="col-span-3 text-center">
                        <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Total Weighted</div>
                        <div className="text-2xl font-bold text-slate-700">{formatMoneyCompact(totalWeightedValue)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Comparison Banner */}
            {comparisonSnapshot && (() => {
              const dealCountDiff = activeDeals.length - (comparisonSnapshot.dealCount || 0);
              const pipelineDiff = totalPipelineValue - (comparisonSnapshot.totalPipelineValue || 0);
              const weightedDiff = weightedPipelineValue - (comparisonSnapshot.totalWeightedValue || 0);
              return (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-purple-700">
                      <HistoryIcon />
                      <span className="text-sm font-medium">
                        Comparing with: <strong>{comparisonSnapshot.snapshotName}</strong>
                      </span>
                    </div>
                    <button
                      onClick={() => setComparisonSnapshot(null)}
                      className="text-purple-500 hover:text-purple-700 text-sm flex items-center gap-1"
                    >
                      <XIcon /> Clear
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {/* Deal Count Change */}
                    <div className="bg-white rounded-lg p-3 border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Deal Count</div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-slate-800">{activeDeals.length}</span>
                        {dealCountDiff !== 0 && (
                          <span className={`text-sm font-bold flex items-center gap-1 ${dealCountDiff > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {dealCountDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            {dealCountDiff > 0 ? '+' : ''}{dealCountDiff}
                          </span>
                        )}
                        {dealCountDiff === 0 && <span className="text-sm text-slate-400">No change</span>}
                      </div>
                      <div className="text-[10px] text-purple-400 mt-1">was {comparisonSnapshot.dealCount || 0}</div>
                    </div>
                    {/* Pipeline Value Change */}
                    <div className="bg-white rounded-lg p-3 border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Pipeline Value</div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-slate-800">{formatMoneyCompact(totalPipelineValue)}</span>
                        {pipelineDiff !== 0 && (
                          <span className={`text-sm font-bold flex items-center gap-1 ${pipelineDiff > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {pipelineDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            {pipelineDiff > 0 ? '+' : ''}{formatMoneyCompact(pipelineDiff)}
                          </span>
                        )}
                        {pipelineDiff === 0 && <span className="text-sm text-slate-400">No change</span>}
                      </div>
                      <div className="text-[10px] text-purple-400 mt-1">was {formatMoneyCompact(comparisonSnapshot.totalPipelineValue || 0)}</div>
                    </div>
                    {/* Weighted Value Change */}
                    <div className="bg-white rounded-lg p-3 border border-purple-100">
                      <div className="text-xs text-purple-600 font-medium mb-1">Weighted Value</div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-slate-800">{formatMoneyCompact(weightedPipelineValue)}</span>
                        {weightedDiff !== 0 && (
                          <span className={`text-sm font-bold flex items-center gap-1 ${weightedDiff > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {weightedDiff > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                            {weightedDiff > 0 ? '+' : ''}{formatMoneyCompact(weightedDiff)}
                          </span>
                        )}
                        {weightedDiff === 0 && <span className="text-sm text-slate-400">No change</span>}
                      </div>
                      <div className="text-[10px] text-purple-400 mt-1">was {formatMoneyCompact(comparisonSnapshot.totalWeightedValue || 0)}</div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Deal Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="font-bold text-gray-800 text-lg">Active Opportunities</h3>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                    title="Export to CSV"
                  >
                    <DownloadIcon />
                    Export
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                    title="Print / Save as PDF"
                  >
                    <PrinterIcon />
                    Print
                  </button>
                  <button
                    onClick={handleRequestUpdates}
                    className="flex items-center gap-1.5 text-xs font-medium bg-white border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <MailIcon />
                    Request Updates
                  </button>
                  <span className="text-sm text-gray-500">{activeDeals.length} deals</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500 font-semibold">
                    <tr>
                      <th className="w-10"></th>
                      <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 select-none min-w-[280px]" onClick={() => handleSort('title')}>
                        <div className="flex items-center">Client <SortIcon column="title" sortConfig={sortConfig} /></div>
                      </th>
                      <th className="px-4 py-3 text-center">Type</th>
                      <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort('owner')}>
                        <div className="flex items-center">Owner <SortIcon column="owner" sortConfig={sortConfig} /></div>
                      </th>
                      <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort('division')}>
                        <div className="flex items-center">Department <SortIcon column="division" sortConfig={sortConfig} /></div>
                      </th>
                      <th className="px-6 py-3 text-center">Stage</th>
                      <th className="px-6 py-3 text-center cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort('probability')}>
                        <div className="flex items-center justify-center">Prob. <SortIcon column="probability" sortConfig={sortConfig} /></div>
                      </th>
                      <th className="px-6 py-3 text-center cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort('expectedClose')}>
                        <div className="flex items-center justify-center">Exp. Close <SortIcon column="expectedClose" sortConfig={sortConfig} /></div>
                      </th>
                      <th className="px-6 py-3 text-right cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleSort('value')}>
                        <div className="flex items-center justify-end">Value <SortIcon column="value" sortConfig={sortConfig} /></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeDeals.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="p-10 text-center text-gray-400">
                          No deals match criteria
                        </td>
                      </tr>
                    ) : (
                      activeDeals.map(deal => (
                        <DealRow
                          key={deal.id}
                          deal={deal}
                          isExpanded={expandedDealId === deal.id}
                          onToggle={() => setExpandedDealId(expandedDealId === deal.id ? null : deal.id)}
                          onUpdateDeal={() => fetchDeals(false)}
                          canSave={connectionHealth.appDbConnected}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeView === 'kpis' && (
          <KPIView
            deals={deals}
            activityData={activityData}
            kpiGoals={kpiGoals}
            kpiTimeframe={kpiTimeframe}
            setKpiTimeframe={setKpiTimeframe}
            filterDivision={filters.division}
            filterOwner={filters.owner}
            filterDateFrom={filters.dateFrom}
            filterDateTo={filters.dateTo}
            userDepartments={userDepartments}
          />
        )}

        {activeView === 'meetings' && (
          <MeetingsView
            deals={deals}
            noteMeetings={noteMeetings}
            filterDivision={filters.division}
            filterOwner={filters.owner}
            filterStages={filterStages}
            userDepartments={userDepartments}
          />
        )}

        {activeView === 'leaderboard' && (
          <LeaderboardView
            deals={deals}
            filterDivision={filters.division}
            filterOwner={filters.owner}
            filterDateFrom={filters.dateFrom}
            filterDateTo={filters.dateTo}
            userDepartments={userDepartments}
          />
        )}

        {activeView === 'team' && isManager && (
          <TeamView
            deals={deals}
            activityData={activityData}
            kpiGoals={kpiGoals}
            userDepartments={userDepartments}
            onSaveGoal={saveKpiGoal}
            availableDivisions={warehouseDepartments.length > 0 ? warehouseDepartments : uniqueDivisions}
            userEmail={userEmail}
            role={userRole}
          />
        )}

        {activeView === 'admin' && isAdmin && (
          <AdminView
            availableDivisions={warehouseDepartments.length > 0 ? warehouseDepartments : uniqueDivisions}
            allActivityOwners={uniqueOwners}
            userDepartments={userDepartments}
            onSaveDepartment={saveUserDepartment}
          />
        )}
      </main>
    </div>
  );
}

export default App;
